// FrontEnd/my-app/src/crm/components/CommercialDocumentModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import {
  LuFileText,
  LuX,
  LuCheck,
  LuSearch,
  LuPlus,
  LuExternalLink,
  LuShieldCheck,
  LuDownload,
  LuEye,
  LuClock,
  LuBuilding2,
  LuUser,
  LuCalendar,
  LuPencil,
  LuSave,
  LuPrinter,
  LuLayers,
  LuChevronDown
} from 'react-icons/lu';

/**
 * Commercial Document Modal
 * Handles:
 * 1. Linking existing MSA/PO to Onboarding step
 * 2. Creating a new Draft MSA/PO
 * 3. Native In-System Signing with complete document preview & typed name
 * 4. Importing an already executed document
 */
const CommercialDocumentModal = ({
  isOpen,
  onClose,
  initialMode = 'LINK_OR_CREATE', // 'LINK_OR_CREATE' | 'CREATE_DRAFT' | 'VIEW_AND_SIGN' | 'EDIT' | 'IMPORT'
  autoEdit = false,
  documentId = null,
  documentTypeCode = 'MSA',
  counterpartySide = 'SUPPLIER', // 'SUPPLIER' | 'CLIENT'
  counterpartyCompanyId = null,
  counterpartyCompanyName = '',
  governingEntityId = 4, // Default Prophecy entity (Prophecy Consulting Inc)
  governingEntityName = 'Prophecy Consulting Inc',
  candidateContext = null, // { candidateName, jobTitle, billRate, ... }
  onLinkedSuccess = null,
  onCreatedSuccess = null
}) => {
  const [activeTab, setActiveTab] = useState('LINK'); // 'LINK' | 'DRAFT' | 'IMPORT'
  const [loading, setLoading] = useState(false);
  const [existingDocs, setExistingDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // All companies available for counterparty selection (all companies from CRM)
  const filteredCounterpartyCompanies = useMemo(() => {
    if (!companies || companies.length === 0) return [];
    return [...companies].sort((a, b) => {
      // Prioritize counterparty side matches at the top (e.g. Suppliers first for Supplier agreements)
      if (counterpartySide === 'SUPPLIER') {
        const aSup = (a.IsSupplier || a.CompanyType?.toUpperCase() === 'SUPPLIER') ? 1 : 0;
        const bSup = (b.IsSupplier || b.CompanyType?.toUpperCase() === 'SUPPLIER') ? 1 : 0;
        if (bSup !== aSup) return bSup - aSup;
      } else if (counterpartySide === 'CLIENT') {
        const aCli = (a.IsClient || a.CompanyType?.toUpperCase() === 'CLIENT') ? 1 : 0;
        const bCli = (b.IsClient || b.CompanyType?.toUpperCase() === 'CLIENT') ? 1 : 0;
        if (bCli !== aCli) return bCli - aCli;
      }
      return (a.DisplayName || a.CompanyName || '').localeCompare(b.DisplayName || b.CompanyName || '');
    });
  }, [companies, counterpartySide]);

  // Draft form state
  const [draftForm, setDraftForm] = useState({
    documentTypeCode: documentTypeCode || 'MSA',
    counterpartySide: counterpartySide || 'SUPPLIER',
    governingEntityId: governingEntityId || 4,
    counterpartyCompanyId: counterpartyCompanyId || '',
    title: '',
    effectiveFromDate: new Date().toISOString().substring(0, 10),
    effectiveToDate: '',
    autoRenew: true,
    renewalTermMonths: 12,
    renewalNoticeDays: 30,
    templateId: '',
    notes: '',
    // PO specific
    consultantName: candidateContext?.candidateName || '',
    endClientName: candidateContext?.endClientName || '',
    workLocation: '',
    startDate: candidateContext?.startDate || new Date().toISOString().substring(0, 10),
    durationText: '06+ months',
    billRate: (counterpartySide === 'SUPPLIER'
      ? (candidateContext?.supplierRate || candidateContext?.billRate || '')
      : (candidateContext?.clientRate || candidateContext?.billRate || '')),
    billRateType: 'HOURLY',
    currencyCode: 'USD',
    paymentTermDays: 60,
    vmsFeePct: candidateContext?.vmsPct || 5.5
  });

  // Searchable counterparty dropdown state
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const companyDropdownRef = useRef(null);

  // Template dropdown state
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) {
        setIsCompanyDropdownOpen(false);
      }
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setIsTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchedCounterpartyCompanies = useMemo(() => {
    const term = companySearchTerm.trim().toLowerCase();
    if (!term) return filteredCounterpartyCompanies;
    return filteredCounterpartyCompanies.filter(c => {
      const name = (c.DisplayName || c.CompanyName || '').toLowerCase();
      const code = (c.CompanyCode || '').toLowerCase();
      const type = (c.CompanyType || (c.IsSupplier ? 'Supplier' : (c.IsClient ? 'Client' : 'Company'))).toLowerCase();
      return name.includes(term) || code.includes(term) || type.includes(term);
    });
  }, [filteredCounterpartyCompanies, companySearchTerm]);

  const selectedCompanyId = draftForm.counterpartyCompanyId || counterpartyCompanyId || '';
  const selectedCompanyObj = useMemo(() => {
    return filteredCounterpartyCompanies.find(c => String(c.CompanyId) === String(selectedCompanyId));
  }, [filteredCounterpartyCompanies, selectedCompanyId]);

  const handleSelectCompany = (c) => {
    const cId = c.CompanyId;
    const resolvedSide = c.IsSupplier ? 'SUPPLIER' : (c.IsClient ? 'CLIENT' : (draftForm.counterpartySide || 'SUPPLIER'));
    setDraftForm(prev => ({
      ...prev,
      counterpartyCompanyId: cId,
      counterpartySide: resolvedSide,
      title: `${prev.documentTypeCode || 'MSA'} — ${c.DisplayName || c.CompanyName || 'Agreement'}`
    }));
    fetchCompanyContacts(cId);
    setIsCompanyDropdownOpen(false);
    setCompanySearchTerm('');
  };

  const handleClearCompany = (e) => {
    e.stopPropagation();
    setDraftForm(prev => ({
      ...prev,
      counterpartyCompanyId: '',
      title: `${prev.documentTypeCode || 'MSA'} — Agreement`
    }));
    setCompanySearchTerm('');
  };

  // Import form state
  const [importForm, setImportForm] = useState({
    title: '',
    effectiveFromDate: '',
    effectiveToDate: '',
    executedAtUtc: '',
    signerName: '',
    signerEmail: '',
    file: null
  });

  // View / Sign State
  const [viewDoc, setViewDoc] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [typedSignName, setTypedSignName] = useState('');
  const [selectedSignerId, setSelectedSignerId] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isEditingTerms, setIsEditingTerms] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    effectiveFromDate: '',
    effectiveToDate: '',
    autoRenew: true,
    renewalTermMonths: 12,
    renewalNoticeDays: 30,
    providerLegalName: '',
    providerAddress: '',
    providerTaxId: '',
    termYears: '3',
    subcontractingCapPct: '40',
    paymentTerms: 'Net 60',
    earlyPaymentDiscount45: '3%',
    earlyPaymentDiscount30: '5%',
    booksRetentionYears: '7',
    nonSolicitationFee: '3,500',
    governingSignerName: 'Senthil Marimuthu',
    governingSignerTitle: 'Chief Executive Officer',
    counterpartySignerName: '',
    counterpartySignerTitle: '',
    counterpartySignerEmail: '',
    customClauses: ''
  });

  const [companyContacts, setCompanyContacts] = useState([]);

  const fetchCompanyContacts = async (compId) => {
    if (!compId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/companies/${compId}/contacts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.data.success && Array.isArray(res.data.data)) {
        setCompanyContacts(res.data.data);
      }
    } catch (err) {
      console.warn('Could not fetch company contacts for modal:', err.message);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchCompanies();
    if (counterpartyCompanyId) {
      fetchCompanyContacts(counterpartyCompanyId);
    }
    if ((initialMode === 'VIEW_AND_SIGN' || initialMode === 'EDIT') && documentId) {
      fetchDocForView(documentId, initialMode === 'EDIT' || autoEdit);
    } else {
      setActiveTab(initialMode === 'CREATE_DRAFT' ? 'DRAFT' : 'LINK');
      fetchExistingAgreements();
      fetchTemplates();
    }
  }, [isOpen, documentId, counterpartyCompanyId, counterpartySide, documentTypeCode, initialMode, autoEdit]);

  useEffect(() => {
    if (counterpartyCompanyId) {
      setDraftForm(prev => ({
        ...prev,
        counterpartyCompanyId: counterpartyCompanyId,
        title: prev.title || `${prev.documentTypeCode || 'MSA'} — ${counterpartyCompanyName || 'Agreement'}`
      }));
    }
    if (candidateContext) {
      const resolvedRate = counterpartySide === 'SUPPLIER'
        ? (candidateContext.supplierRate || candidateContext.billRate || '')
        : (candidateContext.clientRate || candidateContext.billRate || '');
      setDraftForm(prev => ({
        ...prev,
        consultantName: prev.consultantName || candidateContext.candidateName || '',
        endClientName: prev.endClientName || candidateContext.endClientName || '',
        billRate: prev.billRate || resolvedRate || ''
      }));
    }
  }, [counterpartyCompanyId, counterpartyCompanyName, candidateContext, counterpartySide]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && Array.isArray(res.data.data)) {
        setCompanies(res.data.data);
      }
    } catch (e) {
      console.error('Error fetching companies:', e);
    }
  };

  const fetchExistingAgreements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let resolvedCompanyId = counterpartyCompanyId;
      if (!resolvedCompanyId && counterpartyCompanyName && companies.length > 0) {
        const found = companies.find(c =>
          (c.DisplayName && c.DisplayName.toLowerCase() === counterpartyCompanyName.toLowerCase()) ||
          (c.CompanyName && c.CompanyName.toLowerCase() === counterpartyCompanyName.toLowerCase())
        );
        if (found) resolvedCompanyId = found.CompanyId;
      }

      const params = {
        typeCode: documentTypeCode,
        counterpartyCompanyId: resolvedCompanyId || undefined,
        side: counterpartySide || undefined
      };
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (res.data.success) {
        // Filter by counterpartySide to ensure supplier MSA only shows for supplier, and client MSA only for client
        const agreements = (res.data.data || []).filter(doc => {
          if (counterpartySide && doc.CounterpartySide && doc.CounterpartySide.toUpperCase() !== counterpartySide.toUpperCase()) {
            return false;
          }
          return true;
        });
        setExistingDocs(agreements);
      }
    } catch (e) {
      console.error('Error fetching existing agreements:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents/templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { typeCode: documentTypeCode, side: counterpartySide }
      });
      let tList = res.data.success ? (res.data.data || []) : [];
      if (tList.length === 0 && counterpartySide) {
        const fallbackRes = await axios.get(`${BASE_URL}/api/crm/commercial-documents/templates`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { typeCode: documentTypeCode }
        });
        if (fallbackRes.data.success && fallbackRes.data.data?.length > 0) {
          tList = fallbackRes.data.data;
        }
      }
      setTemplates(tList);
      if (tList.length > 0) {
        setDraftForm(prev => ({ ...prev, templateId: tList[0].CommercialTemplateId }));
      }
    } catch (e) {
      console.error('Error fetching templates:', e);
    }
  };

  const fetchDocForView = async (id, shouldEdit = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/crm/commercial-documents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const doc = res.data.data;
        setViewDoc(doc);

        let parsedNotes = {};
        try {
          if (doc.Notes && doc.Notes.trim().startsWith('{')) {
            parsedNotes = JSON.parse(doc.Notes);
          }
        } catch (e) {}

        const defaultProviderAddr = [
          doc.CounterpartyAddressLine1,
          doc.CounterpartyCity,
          doc.CounterpartyState,
          doc.CounterpartyPostalCode
        ].filter(Boolean).join(', ');

        const govSigner = (doc.signers || []).find(s => s.PartySide === 'GOVERNING');
        const cpSigner = (doc.signers || []).find(s => s.PartySide === 'COUNTERPARTY');

        // Ensure Prophecy's address is NEVER used as the provider address fallback
        const rawAddr = parsedNotes.providerAddress || defaultProviderAddr || '';
        const cleanProviderAddr = (rawAddr && rawAddr.toLowerCase().includes('7545 irvine center'))
          ? (defaultProviderAddr && !defaultProviderAddr.toLowerCase().includes('7545 irvine center') ? defaultProviderAddr : '')
          : rawAddr;

        setEditForm({
          title: doc.Title || '',
          effectiveFromDate: doc.EffectiveFromDate ? doc.EffectiveFromDate.split('T')[0] : '',
          effectiveToDate: doc.EffectiveToDate ? doc.EffectiveToDate.split('T')[0] : '',
          autoRenew: doc.AutoRenew ?? true,
          renewalTermMonths: doc.RenewalTermMonths || 12,
          renewalNoticeDays: doc.RenewalNoticeDays || 30,
          providerLegalName: parsedNotes.providerLegalName || doc.CounterpartyLegalName || doc.CounterpartyName || counterpartyCompanyName || '',
          providerAddress: cleanProviderAddr,
          providerTaxId: (parsedNotes.providerTaxId || doc.CounterpartyTaxId || '').replace(/\D/g, '').slice(0, 9),
          termYears: parsedNotes.termYears || '3',
          subcontractingCapPct: parsedNotes.subcontractingCapPct || '40',
          paymentTerms: parsedNotes.paymentTerms || 'Net 60',
          earlyPaymentDiscount45: parsedNotes.earlyPaymentDiscount45 || '3%',
          earlyPaymentDiscount30: parsedNotes.earlyPaymentDiscount30 || '5%',
          booksRetentionYears: parsedNotes.booksRetentionYears || '7',
          nonSolicitationFee: parsedNotes.nonSolicitationFee || '3,500',
          governingSignerName: govSigner?.SignerName || 'Senthil Marimuthu',
          governingSignerTitle: govSigner?.SignerTitle || 'Chief Executive Officer',
          counterpartySignerName: cpSigner?.SignerName || '',
          counterpartySignerTitle: cpSigner?.SignerTitle || '',
          counterpartySignerEmail: cpSigner?.SignerEmail || '',
          customClauses: parsedNotes.customClauses || ''
        });

        if (shouldEdit) {
          setIsEditingTerms(true);
        }

        if (doc.CounterpartyCompanyId || counterpartyCompanyId) {
          fetchCompanyContacts(doc.CounterpartyCompanyId || counterpartyCompanyId);
        }

        // Find pending signer
        const pending = (doc.signers || []).find(s => s.StatusCode !== 'SIGNED');
        if (pending) {
          setSelectedSignerId(pending.SignerId);
          setTypedSignName(pending.SignerName || '');
          // Record view event
          axios.post(`${BASE_URL}/api/crm/commercial-documents/${id}/signers/${pending.SignerId}/view`).catch(() => {});
        }

        // Fetch matching template preview to render full multi-page Word DOCX
        setTemplatePreview(null);
        setPreviewLoading(true);
        const tmplId = doc.TemplateId || (doc.DocumentTypeCode === 'PO' ? 2 : (doc.DocumentTypeCode === 'SOW' ? 3 : 1));
        axios.get(`${BASE_URL}/api/crm/commercial-documents/templates/${tmplId}/preview`)
          .then(tmplRes => {
            if (tmplRes.data?.success) {
              setTemplatePreview(tmplRes.data);
            }
          })
          .catch(tErr => {
            console.warn('Could not fetch template preview for doc view:', tErr);
          })
          .finally(() => {
            setPreviewLoading(false);
          });
      }
    } catch (e) {
      console.error('Error fetching doc details:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAgreementChanges = async () => {
    if (!viewDoc) return;

    // Validation: Federal Tax ID / EIN must be exactly 9 digits
    const cleanedTaxId = (editForm.providerTaxId || '').replace(/\D/g, '');
    if (!cleanedTaxId || cleanedTaxId.length !== 9) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Tax ID / EIN',
        text: 'Federal Tax ID / EIN is required and must contain exactly 9 numbers.'
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const notesPayload = JSON.stringify({
        providerLegalName: editForm.providerLegalName,
        providerAddress: editForm.providerAddress,
        providerTaxId: cleanedTaxId,
        termYears: editForm.termYears,
        subcontractingCapPct: editForm.subcontractingCapPct,
        paymentTerms: editForm.paymentTerms,
        earlyPaymentDiscount45: editForm.earlyPaymentDiscount45,
        earlyPaymentDiscount30: editForm.earlyPaymentDiscount30,
        booksRetentionYears: editForm.booksRetentionYears,
        nonSolicitationFee: editForm.nonSolicitationFee,
        customClauses: editForm.customClauses
      });

      const govSigner = (viewDoc.signers || []).find(s => s.PartySide === 'GOVERNING');
      const cpSigner = (viewDoc.signers || []).find(s => s.PartySide === 'COUNTERPARTY');

      const signersPayload = [];
      if (govSigner) {
        signersPayload.push({
          SignerId: govSigner.SignerId,
          SignerName: editForm.governingSignerName,
          SignerTitle: editForm.governingSignerTitle
        });
      }
      if (cpSigner) {
        signersPayload.push({
          SignerId: cpSigner.SignerId,
          SignerName: editForm.counterpartySignerName,
          SignerEmail: editForm.counterpartySignerEmail,
          SignerTitle: editForm.counterpartySignerTitle
        });
      }

      const payload = {
        title: editForm.title || viewDoc.Title,
        effectiveFromDate: editForm.effectiveFromDate || null,
        effectiveToDate: editForm.effectiveToDate || null,
        autoRenew: editForm.autoRenew,
        renewalTermMonths: parseInt(editForm.renewalTermMonths, 10) || 12,
        renewalNoticeDays: parseInt(editForm.renewalNoticeDays, 10) || 30,
        notes: notesPayload,
        signers: signersPayload
      };

      const res = await axios.patch(`${BASE_URL}/api/crm/commercial-documents/${viewDoc.CommercialDocumentId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Agreement Updated',
          text: 'Agreement terms and placeholders have been saved successfully.',
          timer: 1500,
          showConfirmButton: false
        });
        setIsEditingTerms(false);
        fetchDocForView(viewDoc.CommercialDocumentId);
        if (onCreatedSuccess) onCreatedSuccess();
        else if (onLinkedSuccess) onLinkedSuccess();
      }
    } catch (err) {
      console.error('Error updating agreement:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const formatUtcDateTime = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' UTC';
  };

  const formatUtcDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
  };

  const getActiveTerms = (doc) => {
    if (!doc) return {};
    let parsed = {};
    try {
      if (doc.Notes && doc.Notes.trim().startsWith('{')) {
        parsed = JSON.parse(doc.Notes);
      }
    } catch (e) {}

    const defaultProviderAddr = [
      doc.CounterpartyAddressLine1,
      doc.CounterpartyCity,
      doc.CounterpartyState,
      doc.CounterpartyPostalCode
    ].filter(Boolean).join(', ');

    const govSigner = (doc.signers || []).find(s => s.PartySide === 'GOVERNING');
    const cpSigner = (doc.signers || []).find(s => s.PartySide === 'COUNTERPARTY');

    return {
      providerLegalName: parsed.providerLegalName || doc.CounterpartyLegalName || doc.CounterpartyName || counterpartyCompanyName || 'Provider Name',
      providerAddress: parsed.providerAddress || defaultProviderAddr || '___________________________________',
      providerTaxId: parsed.providerTaxId || doc.CounterpartyTaxId || '_____________',
      termYears: parsed.termYears || '3',
      subcontractingCapPct: parsed.subcontractingCapPct || '40',
      paymentTerms: parsed.paymentTerms || 'Net 60',
      earlyPaymentDiscount45: parsed.earlyPaymentDiscount45 || '3%',
      earlyPaymentDiscount30: parsed.earlyPaymentDiscount30 || '5%',
      booksRetentionYears: parsed.booksRetentionYears || '7',
      nonSolicitationFee: parsed.nonSolicitationFee || '3,500',
      customClauses: parsed.customClauses || '',
      effectiveDateStr: doc.EffectiveFromDate
        ? new Date(doc.EffectiveFromDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
        : '7th August 2026',
      govSigner: govSigner ? {
        ...govSigner,
        StatusCode: govSigner.StatusCode || govSigner.statusCode,
        SignedAtUtc: govSigner.SignedAtUtc || govSigner.signedAtUtc,
        TypedName: govSigner.TypedName || govSigner.typedName || govSigner.SignerName,
        SignerName: govSigner.SignerName || govSigner.name,
        SignerTitle: govSigner.SignerTitle || govSigner.title || 'Chief Executive Officer',
        SignatureImage: govSigner.SignatureImage || govSigner.signatureImage || null,
        SelectedFont: govSigner.SelectedFont || govSigner.selectedFont || null,
        SigningMethod: govSigner.SigningMethod || govSigner.signingMethod || null
      } : null,
      cpSigner: cpSigner ? {
        ...cpSigner,
        StatusCode: cpSigner.StatusCode || cpSigner.statusCode,
        SignedAtUtc: cpSigner.SignedAtUtc || cpSigner.signedAtUtc,
        TypedName: cpSigner.TypedName || cpSigner.typedName || cpSigner.SignerName,
        SignerName: cpSigner.SignerName || cpSigner.name,
        SignerTitle: cpSigner.SignerTitle || cpSigner.title || 'Authorized Representative',
        SignatureImage: cpSigner.SignatureImage || cpSigner.signatureImage || null,
        SelectedFont: cpSigner.SelectedFont || cpSigner.selectedFont || null,
        SigningMethod: cpSigner.SigningMethod || cpSigner.signingMethod || null
      } : null
    };
  };

  // Helper to format HTML from Word docx to match native Word alignment & styling
  const formatDocumentHtml = (html) => {
    if (!html) return '';
    let res = html;

    // 1. Format clause headings in <ol> to be bold & underlined like Word docx:
    res = res.replace(/<li>([A-Za-z0-9\s&,/-]+?\.)(\s+)/g, '<li><strong style="text-decoration: underline; font-weight: 700;">$1</strong>$2');

    // 2. Format title headings
    res = res.replace(
      /<p><strong>(MASTER SERVICES AGREEMENT|VENDOR WORK ORDER|PURCHASE ORDER.*?)<\/strong><\/p>/gi,
      '<div class="doc-title-heading">$1</div>'
    );

    // 3. Format "WITNESSETH" section header
    res = res.replace(
      /<p><strong>WITNESSETH<\/strong><\/p>/gi,
      '<p class="doc-section-heading"><strong>WITNESSETH</strong></p>'
    );

    return res;
  };

  // Helper to split document HTML into authentic multi-page layout matching Word docx
  const getDocPages = (htmlStr, previewObj) => {
    if (previewObj?.pages && Array.isArray(previewObj.pages) && previewObj.pages.length > 1) {
      return previewObj.pages;
    }
    if (!htmlStr) return [];

    const splitPhrases = [
      'Requisite Skills.',
      'Legal Compliance.',
      'Provider represents and warrants that it will use best technical practices',
      'Non-Solicitation.',
      'notice to the other party as provided herein.',
      'the parties as if they were originals.'
    ];

    let remaining = htmlStr;
    const rawPages = [];

    for (const phrase of splitPhrases) {
      const idx = remaining.indexOf(phrase);
      if (idx !== -1) {
        let cutIdx = idx;
        const liBefore = remaining.lastIndexOf('<li', idx);
        const pBefore = remaining.lastIndexOf('<p', idx);
        const bestBefore = Math.max(liBefore, pBefore);
        if (bestBefore !== -1 && idx - bestBefore < 120) {
          cutIdx = bestBefore;
        }
        rawPages.push(remaining.substring(0, cutIdx));
        remaining = remaining.substring(cutIdx);
      }
    }
    if (remaining) {
      rawPages.push(remaining);
    }

    return rawPages.map((pageHtml, idx) => {
      let fixed = pageHtml;
      const firstLi = fixed.indexOf('<li');
      const firstOl = fixed.indexOf('<ol');
      if (firstLi !== -1 && (firstOl === -1 || firstLi < firstOl)) {
        let startNum = 1;
        if (idx === 1) startNum = 4;
        else if (idx === 2) startNum = 9;
        else if (idx === 4) startNum = 20;
        else if (idx === 5) startNum = 25;
        fixed = `<ol start="${startNum}">` + fixed;
      }

      const olOpenCount = (fixed.match(/<ol[^>]*>/g) || []).length;
      const olCloseCount = (fixed.match(/<\/ol>/g) || []).length;
      if (olOpenCount > olCloseCount) {
        fixed += '</ol>'.repeat(olOpenCount - olCloseCount);
      }
      if (olCloseCount > olOpenCount) {
        fixed = '<ol>'.repeat(olCloseCount - olOpenCount) + fixed;
      }

      return fixed;
    });
  };

  // Helper to interpolate dynamic party variables and digital signatures into pages
  const interpolateDocData = (pageHtml, terms, doc, pageIdx, totalPages) => {
    if (!pageHtml) return '';
    let res = pageHtml;

    const isPO = doc?.DocumentTypeCode === 'PO' || pageHtml.includes('VENDOR WORK ORDER') || pageHtml.includes('PURCHASE ORDER');

    // === PO DOCUMENT INTERPOLATION ===
    if (isPO) {
      const po = doc?.poDetail || {};
      const billRateVal = (po.BillRate != null && po.BillRate !== '')
        ? `$${Number(po.BillRate).toFixed(2)} / ${po.BillRateType || 'HOURLY'}`
        : '$0.00 / HOURLY';

      // 1. Preamble provider legal name
      if (terms.providerLegalName) {
        res = res.replace(
          /(signed between (?:<strong>)?Prophecy Consulting Inc(?:<\/strong>)? and\s*(?:<strong>)?)(_{4,}|\[.*?\])(?:<\/strong>)?/gi,
          `$1${terms.providerLegalName}</strong>`
        );
        res = res.replace(/and\s*<strong>_{5,}<\/strong>/gi, `and <strong>${terms.providerLegalName}</strong>`);
        res = res.replace(/and\s*_{5,}/gi, `and <strong>${terms.providerLegalName}</strong>`);
      }

      // 2. Table cells
      res = res.replace(
        /(Consultant[’']s Full Name<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${po.ConsultantName || 'Sai Shashank Rachumalla'}</strong></p>$3`
      );
      res = res.replace(
        /(Client Name<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${po.EndClientName || 'Direct Engagement'}</strong></p>$3`
      );

      let workLocIndex = 0;
      res = res.replace(/(Work Location<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi, (match, p1, p2, p3) => {
        workLocIndex++;
        if (workLocIndex === 1) {
          return `${p1}<p><strong>${po.WorkLocation || 'Remote / Client Site'}</strong></p>${p3}`;
        } else {
          const secondVal = po.StartDate ? `Start Date: ${new Date(po.StartDate).toLocaleDateString()}` : (po.WorkLocation || 'Remote / Client Site');
          return `${p1}<p><strong>${secondVal}</strong></p>${p3}`;
        }
      });

      res = res.replace(
        /(Duration<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${po.DurationText || '06+ months'}</strong></p>$3`
      );

      res = res.replace(
        /(Bill Rate<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${billRateVal}</strong></p>$3`
      );

      // 3. Payment Term condition
      res = res.replace(
        /Payment Term:\s*_{4,}\s*days/gi,
        `Payment Term: <strong>${po.PaymentTermDays || 60} days (Net ${po.PaymentTermDays || 60})</strong>`
      );
      res = res.replace(
        /Payment Term:\s*\[.*?\]\s*days/gi,
        `Payment Term: <strong>${po.PaymentTermDays || 60} days (Net ${po.PaymentTermDays || 60})</strong>`
      );

      // 4. Signatures execution block at bottom of PO
      const govSigned = terms.govSigner?.StatusCode === 'SIGNED';
      const cpSigned = terms.cpSigner?.StatusCode === 'SIGNED';
      const govDate = terms.govSigner?.SignedAtUtc ? formatUtcDate(terms.govSigner.SignedAtUtc) : '';
      const cpDate = terms.cpSigner?.SignedAtUtc ? formatUtcDate(terms.cpSigner.SignedAtUtc) : '';
      const govTime = terms.govSigner?.SignedAtUtc ? formatUtcDateTime(terms.govSigner.SignedAtUtc) : '';
      const cpTime = terms.cpSigner?.SignedAtUtc ? formatUtcDateTime(terms.cpSigner.SignedAtUtc) : '';
      const cpName = terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName;
      const cpTitle = terms.cpSigner?.SignerTitle || 'Authorized Representative';

      const poSignaturesHtml = `
<p style="margin-top: 24px; font-weight: 600;">The undersigned has/have read, understand(s) and agree(s) to terms and conditions herein.</p>
<table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
  <tbody>
    <tr>
      <td style="width: 50%; font-weight: 700; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 8px 12px;">
        Accepted by: <strong>Prophecy Consulting Inc</strong>
      </td>
      <td style="width: 50%; font-weight: 700; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 8px 12px;">
        Accepted by: <strong>${terms.providerLegalName}</strong>
      </td>
    </tr>
    <tr>
      <td style="border: 1px solid #94a3b8; padding: 12px;">
        ${govSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.govSigner?.SignatureImage ? `
              <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.govSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${terms.govSigner.TypedName || 'Senthil Marimuthu'}</span>
            `}
            <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${govTime}</span>
          </div>
        ` : `<div style="border-bottom: 1px solid #94a3b8; width: 180px; margin: 16px 0 6px;"><span style="font-size: 0.75rem; color: #94a3b8;">By: ______________________</span></div>`}
        <div style="font-size: 0.85rem; margin-top: 8px;">
          <div><strong>Name:</strong> Senthil Marimuthu</div>
          <div><strong>Title:</strong> Chief Executive Officer</div>
          <div><strong>Date:</strong> ${govSigned ? `<strong>${govDate}</strong>` : '_______________'}</div>
        </div>
      </td>
      <td style="border: 1px solid #94a3b8; padding: 12px;">
        ${cpSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.cpSigner?.SignatureImage ? `
              <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.cpSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${cpName}</span>
            `}
            <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${cpTime}</span>
          </div>
        ` : `<div style="border-bottom: 1px solid #94a3b8; width: 180px; margin: 16px 0 6px;"><span style="font-size: 0.75rem; color: #94a3b8;">By: ______________________</span></div>`}
        <div style="font-size: 0.85rem; margin-top: 8px;">
          <div><strong>Name:</strong> <strong>${cpName}</strong></div>
          <div><strong>Title:</strong> <strong>${cpTitle}</strong></div>
          <div><strong>Date:</strong> ${cpSigned ? `<strong>${cpDate}</strong>` : '_______________'}</div>
        </div>
      </td>
    </tr>
  </tbody>
</table>`;

      res = res.replace(/<p>The undersigned has\/have read, understand\(s\) and agree\(s\)[\s\S]*?Date:\s*<\/p>/gi, poSignaturesHtml);
      return res;
    }

    // === MSA DOCUMENT INTERPOLATION ===
    // Page 1: Effective date, Subcontractor name, Address, Tax ID
    if (pageIdx === 0) {
      if (terms.effectiveDateStr) {
        res = res.replace(/7<sup>th<\/sup>\s*August\s*2026/gi, terms.effectiveDateStr);
        res = res.replace(/August\s+7,?\s+2026/gi, terms.effectiveDateStr);
        res = res.replace(/\[\s*Date\s*\]/gi, terms.effectiveDateStr);
      }
      if (terms.providerLegalName) {
        res = res.replace(/_____________(\s*<\/strong>\s*located\s*at)/gi, `<strong>${terms.providerLegalName}</strong>$1`);
        res = res.replace(/\[\s*Subcontractor\s+Name\s*\]/gi, terms.providerLegalName);
        res = res.replace(/\[\s*Provider\s+Name\s*\]/gi, terms.providerLegalName);
      }
      if (terms.providerAddress) {
        res = res.replace(/_{5,}(\s*\(“Provider”\))/gi, `${terms.providerAddress}$1`);
        res = res.replace(/\[\s*Provider\s+Address\s*\]/gi, terms.providerAddress);
      }
      if (terms.providerTaxId) {
        res = res.replace(/(Federal Tax ID<strong>:\s*)(_{5,})(\.<\/strong>)/gi, `$1<strong>${terms.providerTaxId}</strong>$3`);
        res = res.replace(/\[\s*Federal\s+Tax\s+ID\s*\]/gi, terms.providerTaxId);
      }
    }

    // Page 6: Notice addresses
    if (pageIdx === 5 || (totalPages <= 6 && pageIdx === totalPages - 2)) {
      if (terms.providerLegalName) {
        res = res.replace(
          /If to Provider:\s*<\/p><p><strong>_{5,}<\/strong><\/p><p><strong>_{5,}<\/strong><\/p><p><strong>_{5,}<\/strong>/gi,
          `If to Provider:</p><p><strong>${terms.providerLegalName}</strong></p><p><strong>${terms.providerAddress || '___________________________'}</strong></p>`
        );
      }
    }

    // Last Page (Signatures table)
    if (pageIdx === totalPages - 1 || pageHtml.includes('IN WITNESS WHEREOF')) {
      const govSigned = terms.govSigner?.StatusCode === 'SIGNED';
      const cpSigned = terms.cpSigner?.StatusCode === 'SIGNED';
      const govDate = terms.govSigner?.SignedAtUtc ? formatUtcDate(terms.govSigner.SignedAtUtc) : '';
      const cpDate = terms.cpSigner?.SignedAtUtc ? formatUtcDate(terms.cpSigner.SignedAtUtc) : '';
      const govTime = terms.govSigner?.SignedAtUtc ? formatUtcDateTime(terms.govSigner.SignedAtUtc) : '';
      const cpTime = terms.cpSigner?.SignedAtUtc ? formatUtcDateTime(terms.cpSigner.SignedAtUtc) : '';
      const govName = terms.govSigner?.TypedName || terms.govSigner?.SignerName || 'Senthil Marimuthu';
      const cpName = terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName;
      const govTitle = terms.govSigner?.SignerTitle || 'Chief Executive Officer';
      const cpTitle = terms.cpSigner?.SignerTitle || 'Authorized Representative';
      const providerCompName = terms.providerLegalName || 'Tata Consultancy Service';

      const signaturesTableHtml = `
<table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
  <tbody>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p><strong>Prophecy Consulting Inc</strong></p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p><strong>${providerCompName}</strong></p></td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; vertical-align: top; background-color: #ffffff;">
        ${govSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.govSigner?.SignatureImage ? `
              <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.govSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${govName}</span>
            `}
            <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${govTime}</span>
          </div>
        ` : '<p>By ______________________</p>'}
      </td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; vertical-align: top; background-color: #ffffff;">
        ${cpSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.cpSigner?.SignatureImage ? `
              <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.cpSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${cpName}</span>
            `}
            <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${cpTime}</span>
          </div>
        ` : '<p>By ______________________</p>'}
      </td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Name: <strong>${govName}</strong></p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Name: <strong>${cpName}</strong></p></td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Title: <strong>${govTitle}</strong></p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Title: <strong>${cpTitle}</strong></p></td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Date: ${govSigned ? `<strong>${govDate}</strong>` : '_______________'}</p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Date: ${cpSigned ? `<strong>${cpDate}</strong>` : '___________________'}</p></td>
    </tr>
  </tbody>
</table>`;

      if (res.includes('<table')) {
        res = res.replace(/<table[\s\S]*?<\/table>/gi, signaturesTableHtml.trim());
      } else {
        res += signaturesTableHtml;
      }
    }

    return res;
  };

  // Full authentic pages fallback matching Word docx template
  const getFallbackDocPages = (terms, doc) => {
    if (doc?.DocumentTypeCode === 'PO') {
      const po = doc?.poDetail || {};
      const billRateVal = (po.BillRate != null && po.BillRate !== '')
        ? `$${Number(po.BillRate).toFixed(2)} / ${po.BillRateType || 'HOURLY'}`
        : '$0.00 / HOURLY';
      const govSigned = terms.govSigner?.StatusCode === 'SIGNED';
      const cpSigned = terms.cpSigner?.StatusCode === 'SIGNED';
      const govDate = terms.govSigner?.SignedAtUtc ? formatUtcDate(terms.govSigner.SignedAtUtc) : '';
      const cpDate = terms.cpSigner?.SignedAtUtc ? formatUtcDate(terms.cpSigner.SignedAtUtc) : '';
      const govTime = terms.govSigner?.SignedAtUtc ? formatUtcDateTime(terms.govSigner.SignedAtUtc) : '';
      const cpTime = terms.cpSigner?.SignedAtUtc ? formatUtcDateTime(terms.cpSigner.SignedAtUtc) : '';
      const cpName = terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName;
      const cpTitle = terms.cpSigner?.SignerTitle || 'Authorized Representative';

      return [
        `<div class="doc-title-heading">VENDOR WORK ORDER</div>
<p style="text-align: justify;">This work order is being issued in accordance with the Master Services Agreement signed between <strong>Prophecy Consulting Inc</strong> and <strong>${terms.providerLegalName}</strong></p>
<table style="width: 100%; border-collapse: collapse; margin: 18px 0;">
  <tbody>
    <tr><td style="width: 32%; font-weight: 600; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;">Consultant’s Full Name</p></td><td style="border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;"><strong>${po.ConsultantName || 'Sai Shashank Rachumalla'}</strong></p></td></tr>
    <tr><td style="width: 32%; font-weight: 600; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;">Client Name</p></td><td style="border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;"><strong>${po.EndClientName || 'Direct Engagement'}</strong></p></td></tr>
    <tr><td style="width: 32%; font-weight: 600; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;">Work Location</p></td><td style="border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;"><strong>${po.WorkLocation || 'Remote / Client Site'}</strong></p></td></tr>
    <tr><td style="width: 32%; font-weight: 600; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;">Work Location / Start Date</p></td><td style="border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;"><strong>${po.StartDate ? new Date(po.StartDate).toLocaleDateString() : (po.WorkLocation || 'Remote / Client Site')}</strong></p></td></tr>
    <tr><td style="width: 32%; font-weight: 600; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;">Duration</p></td><td style="border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;"><strong>${po.DurationText || '06+ months'}</strong></p></td></tr>
    <tr><td style="width: 32%; font-weight: 600; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;">Bill Rate</p></td><td style="border: 1px solid #94a3b8; padding: 10px 14px;"><p style="margin: 0;"><strong>${billRateVal}</strong></p></td></tr>
  </tbody>
</table>
<p style="font-weight: 700; margin-top: 18px; margin-bottom: 8px;">CONDITIONS:</p>
<ol style="padding-left: 28px; line-height: 1.65; margin: 0 0 20px 0;">
  <li style="margin-bottom: 10px;">Payment Term: <strong>${po.PaymentTermDays || 60} days (Net ${po.PaymentTermDays || 60})</strong></li>
  <li style="margin-bottom: 10px;">The start date is tentative, subject to successful completion of BGC with no negative remarks and is also subject to confirmation on the start date from the client as per their portal.</li>
  <li style="margin-bottom: 10px;">LCA amendment, if applicable, should be filed with proper work location address and a certified copy has to be sent before starting the consultant on the project.</li>
  <li style="margin-bottom: 10px;">If the Vendor fails to complete the onboarding of the Consultant mentioned above for any reason after initiating the Background Check (BGC) and/or Drug Test, Prophecy reserves the right to recover the actual cost of the BGC and/or Drug Test from the Vendor. Additionally, Prophecy will deduct the BGC cost from the Vendor’s first payment.</li>
  <li style="margin-bottom: 10px;">The last payment due to the vendor for the above-mentioned Consultant(s) will only be sent once the Prophecy Accounts team has reconciled the file to ensure that no payment discrepancies have been found. Prophecy will inform the Supplier in case of any issues. Prophecy shall not be held liable for any delays arising as a result of the discrepancy.</li>
  <li style="margin-bottom: 10px;">Payment terms will be established based on the date the complete and undisputed invoice, along with the approved timesheet, is received. In line with our company policy, invoices must be submitted monthly to <strong>accounts@prophecytechs.com</strong>.</li>
</ol>
<p style="margin-top: 24px; font-weight: 600;">The undersigned has/have read, understand(s) and agree(s) to terms and conditions herein.</p>
<table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
  <tbody>
    <tr>
      <td style="width: 50%; font-weight: 700; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 8px 12px;">
        Accepted by: <strong>Prophecy Consulting Inc</strong>
      </td>
      <td style="width: 50%; font-weight: 700; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 8px 12px;">
        Accepted by: <strong>${terms.providerLegalName}</strong>
      </td>
    </tr>
    <tr>
      <td style="border: 1px solid #94a3b8; padding: 12px;">
        ${govSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.govSigner?.SignatureImage ? `
              <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.govSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${terms.govSigner.TypedName || 'Senthil Marimuthu'}</span>
            `}
            <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${govTime}</span>
          </div>
        ` : `<div style="border-bottom: 1px solid #94a3b8; width: 180px; margin: 16px 0 6px;"><span style="font-size: 0.75rem; color: #94a3b8;">By: ______________________</span></div>`}
        <div style="font-size: 0.85rem; margin-top: 8px;">
          <div><strong>Name:</strong> Senthil Marimuthu</div>
          <div><strong>Title:</strong> Chief Executive Officer</div>
          <div><strong>Date:</strong> ${govSigned ? `<strong>${govDate}</strong>` : '_______________'}</div>
        </div>
      </td>
      <td style="border: 1px solid #94a3b8; padding: 12px;">
        ${cpSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.cpSigner?.SignatureImage ? `
              <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.cpSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${cpName}</span>
            `}
            <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${cpTime}</span>
          </div>
        ` : `<div style="border-bottom: 1px solid #94a3b8; width: 180px; margin: 16px 0 6px;"><span style="font-size: 0.75rem; color: #94a3b8;">By: ______________________</span></div>`}
        <div style="font-size: 0.85rem; margin-top: 8px;">
          <div><strong>Name:</strong> <strong>${cpName}</strong></div>
          <div><strong>Title:</strong> <strong>${cpTitle}</strong></div>
          <div><strong>Date:</strong> ${cpSigned ? `<strong>${cpDate}</strong>` : '_______________'}</div>
        </div>
      </td>
    </tr>
  </tbody>
</table>`
      ];
    }

    return [
      // Page 1
      `<div class="doc-title-heading">MASTER SERVICES AGREEMENT</div>
<p>THIS MASTER SERVICES AGREEMENT (“Agreement”) is made as of <strong>${terms.effectiveDateStr}</strong> by and between <strong>Prophecy Consulting Inc,</strong> a CA Corporation located at <strong>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</strong> (“<strong>Client</strong>”) with Federal Tax ID: <strong>46-5026539</strong>, and <strong>${terms.providerLegalName}</strong> located at <strong>${terms.providerAddress}</strong> (“<strong>Provider</strong>”) with Federal Tax ID: <strong>${terms.providerTaxId}</strong>.</p>
<p class="doc-section-heading"><strong>WITNESSETH</strong></p>
<p>WHEREAS Provider is an independent contractor who provides consulting services; and</p>
<p>WHEREAS Client wishes to purchase consulting services in form of Contract Employees to be placed on a site of Client; and</p>
<p>WHEREAS, as used herein, the term “Contract Employee(s)” means an individual employed by Provider, temporarily placed with Client pursuant to this Agreement.</p>
<p>NOW, THEREFORE, in consideration of the mutual premises hereinafter set forth and of other good and valuable consideration, the receipt and sufficiency of which is hereby acknowledged, Client and Provider, intending to be legally bound, hereby agree as follows:</p>
<ol>
  <li><strong style="text-decoration: underline; font-weight: 700;">Engagement.</strong> Client hereby engages Provider, on a non-exclusive basis, to provide Services (as defined below) pursuant to one or more purchase orders to be executed by the parties (each a “Purchase Order”), upon the terms and conditions set forth herein, and Provider hereby accepts the engagement. Each Purchase Order shall be mutually agreed upon in writing by Provider and Client before commencement of the work called for by such Purchase Order. A Purchase Order is a request for additional Services and/or changes to Services and shall not be effective unless and until accepted in writing by both parties.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Termination.</strong> This Agreement shall commence on the Effective Date and continue for a term of ${terms.termYears} (${terms.termYears === '3' ? 'three (3)' : terms.termYears}) years with automatic one (1) year renewals unless terminated as set forth herein. Either party may terminate this Agreement with thirty (30) days written notice. Client may terminate immediately for performance issues, insolvency, breach of obligations, or at Client’s Client request. If a breach is capable of remedy, the breaching party has fourteen (14) days after notice to cure. Upon termination, Client may require Provider to continue Assignments through current requisitions or waive placement/transition fees to release workers to Client or its client.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Contract Employees.</strong> Provider shall supply Contract Employees per Client requisitions, ensuring they have requisite skills, work authorization, and complete all background and compliance checks as mandated by Client, including use of specified vendors. Provider shall ensure workers complete all mandatory security and client trainings within two (2) weeks of assignment start. No remote work unless approved in writing. Provider shall not subcontract labor without Client’s prior written approval and total subcontracted personnel shall not exceed ${terms.subcontractingCapPct}% of all Provider personnel assigned. Provider must provide quarterly due diligence and self-assessment forms on ownership, subcontractors, and compliance.</li>
</ol>`,

      // Page 2
      `<ol start="4">
  <li><strong style="text-decoration: underline; font-weight: 700;">Requisite Skills.</strong> Provider specifically acknowledges and agrees that all Contract Employees shall have the requisite knowledge, expertise, experience and training to perform the Services in a timely, competent, and professional manner, and that Client will not be required to provide Contract Employees with any training concerning the manner or methods of performance of the Services, nor will Client provide Contract Employees with any tools or equipment to complete the Services (unless otherwise specified in the applicable Purchase Order).</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Equal Opportunity Employment.</strong> Provider will provide qualified Contract Employees without regard to any protected category under applicable state or federal law, including without limitation race, religion, age, gender, national origin, sexual orientation, physical handicap or marital status.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Independent Contractor.</strong> Provider acts as an independent contractor. Provider and/or Contract Employee will not represent itself as an employee, agent or legal representative of Client. Provider shall be responsible for providing any salaries, benefits, wages, and other forms of compensation to Contract Employees. Contract Employees are not entitled to participate in any Client benefit plans. Provider will promptly reimburse Client for any taxes Client is required to pay or withhold with respect to Contract Employees.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Taxes.</strong> Provider will be responsible for paying all federal, state, municipal, or other taxes as are required by any law, regulation, or ruling, including without limitation federal and state employment taxes related to performance of the Services. Provider is responsible for withholding and reporting to the Internal Revenue Service and applicable state/local taxing authorities any and all wages earned in the performance of the Services by Employees.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Invoicing & Payments.</strong> Provider may submit timesheets entered in the VMS/timesheet system by the Contract Employees and submit invoices based on approved time and expenses. Any unbilled time or expenses of more than ninety (30) days old will be forfeited. Provider will be paid within sixty (60) days from the date of receipt of a correct and undisputed invoice submitted to the Client for the applicable work (“<strong>${terms.paymentTerms} Payment Terms</strong>”).
    <br/><br/>
    <em>Early payment discount option:</em>
    <ul>
      <li><strong>${terms.earlyPaymentDiscount45}</strong> of total invoice amount for Net 45 Payment Terms.</li>
      <li><strong>${terms.earlyPaymentDiscount30}</strong> of total invoice amount for Net 30 Payment Terms.</li>
    </ul>
    Provider has thirty (30) days from availability of the Remittance Report to dispute billing or payment accuracy; failure to dispute is deemed acceptance. Clients may offset overpayments, asset replacement costs, MSP fees, penalties, or any other owed amounts against invoices or future payments. Provider agrees to promptly refund Client for any amounts Client is required to return to its client due to errors, fraud, or overpayment, within five (5) business days of demand. All invoices and payments shall conform strictly to the rates, pricing, and other terms set forth in <strong>Exhibit A (Pricing)</strong>, which is incorporated by reference. Provider may not invoice Client at any rate or charge not authorized in Exhibit A or the applicable Purchase Order without Client’s prior written consent.
  </li>
</ol>`,

      // Page 3
      `<ol start="9">
  <li><strong style="text-decoration: underline; font-weight: 700;">Expenses.</strong> Provider shall be responsible for all expenses incurred in the performance of the Services unless specifically agreed upon by the parties in writing in advance. Only pre-approved expenses reimbursed.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Legal Compliance.</strong> Provider shall comply with all applicable laws and maintain all necessary permits and licenses. Provider represents and warrants compliance with immigration, background check, wage and hour, and tax obligations.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Books and Records.</strong> Provider shall keep and shall preserve for <strong>Seven (${terms.booksRetentionYears}) years</strong> after final completion of all Services, accurate and detailed records of all timesheets, ledgers, books of account, invoices, vouchers, cancelled checks, and other documents or records evidencing or relating to the Services and disbursements charged to Client under this Agreement (collectively, “Books and Records”). Any and all Books and Records must be maintained in accordance with generally accepted accounting principles and must be sufficiently complete and detailed so as to permit an accurate evaluation of the Services provided by Provider under this Agreement. During such ${terms.booksRetentionYears} (${terms.booksRetentionYears === '7' ? 'seven (7)' : terms.booksRetentionYears}) year period, Provider shall give Client and its agents, during normal business hours, access to such Books and Records and Client and its agents shall have the right to make copies of any of the said Books and Records according to what is allowable based on Federal, State, City and HIPAA laws set forth and will not cause a confidentiality breach. Provider shall submit quarterly self-assessments and due diligence declarations to Client. If audit reveals overbilling or other errors, Provider shall refund the amount plus any applicable interest and Client may offset against future invoices.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Confidentiality.</strong> Provider and its Contract Employees may receive Confidential Information. All such information is confidential and may not be disclosed without Client’s written consent. Provider must ensure each Contract Employee signs a nondisclosure agreement acceptable to Client before starting work.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Data Protection.</strong> Provider shall comply with GDPR and applicable data privacy laws, implement SOC 2 Type II / ISO 27001 level controls, and notify Client within forty-eight (48) hours of any security incident or breach. Provider shall appoint a security officer, maintain policies, and provide evidence of compliance on request.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Asset Return.</strong> Provider must ensure Contract Employees return all Client or Client’s client’s assets within two (2) weeks after termination of assignment. Provider shall reimburse the replacement cost of any unreturned or damaged assets within one (1) week, failing which Client may withhold equivalent amounts from invoices.</li>
</ol>`,

      // Page 4
      `<ol start="15">
  <li><strong style="text-decoration: underline; font-weight: 700;">Non-Disclosure of Confidential Information to Third Parties.</strong> Provider will not, during or at any time after termination without prior written authorization of Client, disclose or make use of Confidential Information in any medium, including social media. Provider may not refer to Client, Provider’s status as a vendor for Client, or the Confidential Information either explicitly or implicitly, in social media or otherwise, without prior written authorization of Client.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">E-mail.</strong> Contract Employees shall acquire no privacy rights on Client's electronic mail (e-mail) system. Client reserves the right to monitor and retrieve material transmitted over e-mail, instant message system or any other social medium. Contract Employees shall not send any information with regard to Client to any individual who is not affiliated with Client.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Provider Warranties.</strong> On behalf of Provider and all Contract Employee(s), Provider represents and warrants:
    <ol type="a">
      <li>Provider represents and warrants that it will use best technical practices and perform services in a timely, economical manner.</li>
      <li>All work products will be in accordance with the terms and specifications in this Agreement and applicable Purchase Orders and will substantially comply with Client’s further direction and instructions.</li>
      <li>All work product is original or fully licensed and will not infringe any third-party rights.</li>
      <li>All work product shall be deemed work-for-hire of Client and fully assignable to Client.</li>
      <li>Provider and Contract Employees will comply with Client policies when at Client premises, including any workplace and safety policies and procedures.</li>
      <li>At the time of acceptance, all work and/or services and each deliverable will conform to Client’s specifications and requirements, and Provider will correct or repair, at no cost to Client, any defect, malfunction, or non-conformity that prevents such deliverable from conforming or performing as warranted.</li>
    </ol>
  </li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Intellectual Property.</strong> As a condition of this Agreement, Provider expressly agrees that Client is the exclusive and sole owner of all work product of every kind performed by any Contract Employee on behalf of Client and all works of authorship, inventions, and improvements are deemed works-for-hire of Client. Provider agrees to execute all necessary documents to vest ownership in Client. Provider further agrees to sign (or procure signature from Contract Employee) all lawful papers, execute all documents, including but not limited to, assignments, licenses and applications for applicable patents, copyrights and trademarks, and to do everything possible to vest title in Client and to aid Client, its successors, assigns and legal representatives to obtain and enforce ownership of and proper protection for the above-mentioned intellectual property rights. Provider’s obligations under this section shall continue in full force and effect notwithstanding the expiration or other termination of this Agreement.</li>
</ol>`,

      // Page 5
      `<ol start="19">
  <li><strong style="text-decoration: underline; font-weight: 700;">Indemnification.</strong> Provider shall indemnify, defend, and hold harmless Client, Client’s client, and their affiliates from any claims arising from Provider’s services, including misclassification of workers, wage/tax issues, employment claims, asset losses, tax information errors, or breach of client contract obligations. Provider’s duty to defend is immediately upon notice of a claim.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Breach; Attorney’s Fees.</strong> Failure to submit proper billing or failure to comply with invoicing obligations constitutes a material breach of this Agreement. In the event of a breach, the non-breaching party may terminate immediately and pursue all remedies at law or in equity. In the event of impossibility of performance due to circumstances beyond a party’s control (force majeure), such non-performance shall be excused. The prevailing party in any action shall be entitled from the other party its reasonable attorney fees and other expenses of litigation.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Modifications.</strong> No change or modification of this Agreement shall be valid unless the same is in writing and signed by Provider and Client.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Non-Solicitation.</strong> Provider agrees not to solicit Client employees or Client’s client employees during the Agreement and for twelve (12) months thereafter. Client or Client’s client may hire Provider’s personnel without restriction; a one-time hiring fee of USD <strong>${terms.nonSolicitationFee}</strong> applies if hired within three (3) months of assignment start. Provider waives any non-compete restrictions for such personnel.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Contact with Client.</strong> Except as required for the performance of this Agreement or the provision of Services hereunder, Provider shall not make direct contact with Client’s employees or other contractors in any form, including but not limited to, email, voicemail, and phone or in person. Client shall provide Provider with all Client feedback as needed, relating to Contract Employees’ performance.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Breach of Non-Solicitation.</strong> In the event of Provider’s breach or threatened breach of this Section 24, Client shall be entitled to a preliminary restraining order and an injunction restraining and enjoining Provider from violating its provisions. In addition to the above, Client may pursue all other remedies as allowed by law or in equity.</li>
</ol>`,

      // Page 6
      `<ol start="25">
  <li><strong style="text-decoration: underline; font-weight: 700;">Non-Solicitation of Personnel.</strong> During the term of this Agreement and any Purchase Order hereunder and for a period of one (1) year following termination or expiration thereof, Provider agrees not to (i) solicit or hire, or accept services from Client employees, directly or indirectly, or assist or encourage any third party to do so, except upon Client’s express written consent, or (ii) directly or indirectly request, induce or advise any of Client’s employees to withdraw, curtail or terminate their relationship with Client; provided, however, that the foregoing restriction shall not apply to the employment of any person who responds to any general recruitment advertisement by Provider in the normal course of business, without specifically targeting or approaching Client’s employees.
    <br/><br/>
    In addition, during the term of this Agreement and/or any Purchase Order hereunder, and for a period of one (1) year thereafter, Client may not, either directly or indirectly, recruit or retain the services of any of Provider’s employees, except through Provider or with Provider’s express written consent; provided, however, that the foregoing restriction shall not apply to the employment of any person who responds to any general recruitment advertisement by Client in the normal course of business, without specifically targeting or approaching Provider’s employees.
  </li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Integration.</strong> This Agreement is the entire agreement between the parties with respect to the subject of this Agreement, and no alteration, modification, or interpretation of this Agreement shall be binding unless in writing and signed by the parties.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Notices.</strong> Any notice to be given or to be served upon either party hereto in connection with this Agreement must be in writing and shall be deemed to have been given and received (i) when personally delivered, (ii) two (2) days after it is sent by Federal Express or similar overnight courier, postage prepaid and addressed to the party for whom it is intended, at that party’s address specified below, or (iii) three (3) days after it is sent by certified or registered United States mail, return receipt requested, postage prepaid and addressed to the party for whom it is intended, at that party’s address specified below. Either party may change the place for the giving of notice to it by thirty (30) days prior written notice to the other party as provided herein.
    <p>If to Client:</p>
    <p><strong>Prophecy Consulting Inc</strong></p>
    <p><strong>7545 Irvine Center Drive, Suite 200</strong></p>
    <p><strong>Irvine, CA 92618</strong></p>
    <p>If to Provider:</p>
    <p><strong>${terms.providerLegalName}</strong></p>
    <p><strong>${terms.providerAddress}</strong></p>
  </li>
  <li><strong style="text-decoration: underline; font-weight: 700;">General Provisions.</strong>
    <ol type="a">
      <li><strong style="text-decoration: underline; font-weight: 700;">Severability.</strong> The invalidity of any one or more of the words, phrases, sentences, clauses or paragraphs contained in the Agreement shall not affect the enforceability of the remaining portions of this Agreement or any part hereof, all of which are inserted conditionally on their being valid in law.</li>
      <li><strong style="text-decoration: underline; font-weight: 700;">Successors and Assigns.</strong> Neither this Agreement nor any of the rights or obligations of Provider arising under this Agreement may be assigned or transferred without Client’s express prior written consent.</li>
      <li><strong style="text-decoration: underline; font-weight: 700;">Headings.</strong> The paragraph headings contained in this Agreement are for reference purposes only and shall not affect in any way the meaning or interpretation of Agreement.</li>
      <li><strong style="text-decoration: underline; font-weight: 700;">Governing Law.</strong> This Agreement shall be governed by and interpreted in accordance with the laws of the State of California, excluding any choice of law provision that would apply the laws of any other jurisdiction. Disputes will be escalated to management, then mediation, then binding arbitration under AAA rules. The Superior Court of the State of California located in San Bernardino County shall have exclusive jurisdiction to adjudicate any dispute arising out of or relating to this Agreement, except for injunctive relief for breaches of confidentiality or non-solicitation.</li>
      <li><strong style="text-decoration: underline; font-weight: 700;">Survival.</strong> All rights and obligations hereunder that by their nature are to continue after any expiration or termination of this Agreement, including but not limited to, the confidentiality and indemnification obligations, shall survive any such expiration or termination.</li>
      <li><strong style="text-decoration: underline; font-weight: 700;">Counterparts.</strong> This Agreement may be executed in any number of counterparts, each of which shall be deemed an original and all of which when taken together shall constitute one and the same instrument. Signatures may be delivered electronically or by facsimile and shall be binding upon the parties as if they were originals.</li>
    </ol>
  </li>
</ol>`,

      // Page 7
      `<p>IN WITNESS WHEREOF, the parties hereto have duly executed and delivered this Agreement as of the day and year first above written.</p>
<table>
  <tbody>
    <tr>
      <td><p><strong>Prophecy Consulting Inc</strong></p></td>
      <td><p><font color="#0f172a"><strong>${terms.providerLegalName}</strong></font></p></td>
    </tr>
    <tr>
      <td>${terms.govSigner?.StatusCode === 'SIGNED' ? `
        <p style="margin: 0;">
          ${terms.govSigner.SignatureImage ? `
            <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
          ` : `
            <span style="font-family: ${terms.govSigner.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${terms.govSigner.TypedName || 'Senthil Marimuthu'}</span>
          `}
          <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${formatUtcDateTime(terms.govSigner.SignedAtUtc)}</span>
        </p>
      ` : '<p>By ______________________</p>'}</td>
      <td>${terms.cpSigner?.StatusCode === 'SIGNED' ? `
        <p style="margin: 0;">
          ${terms.cpSigner.SignatureImage ? `
            <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
          ` : `
            <span style="font-family: ${terms.cpSigner.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #1d4ed8; font-weight: 600;">${terms.cpSigner.TypedName || terms.cpSigner.SignerName || terms.providerLegalName}</span>
          `}
          <span style="font-size: 0.72rem; color: #1d4ed8; font-weight: 600;">✓ Digitally Signed on ${formatUtcDateTime(terms.cpSigner.SignedAtUtc)}</span>
        </p>
      ` : '<p>By ______________________</p>'}</td>
    </tr>
    <tr>
      <td><p>Name: <strong>Senthil Marimuthu</strong></p></td>
      <td><p>Name: <strong>${terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName}</strong></p></td>
    </tr>
    <tr>
      <td><p>Title: <strong>Chief Executive Officer</strong></p></td>
      <td><p>Title: <strong>${terms.cpSigner?.SignerTitle || 'Authorized Representative'}</strong></p></td>
    </tr>
    <tr>
      <td><p>Date: ${terms.govSigner?.SignedAtUtc ? `<strong>${formatUtcDate(terms.govSigner.SignedAtUtc)}</strong>` : '_______________'}</p></td>
      <td><p>Date: ${terms.cpSigner?.SignedAtUtc ? `<strong>${formatUtcDate(terms.cpSigner.SignedAtUtc)}</strong>` : '_______________'}</p></td>
    </tr>
  </tbody>
</table>
<div style="margin-top: 24px; padding-top: 14px; border-top: 1px solid #cbd5e1;">
  <div style="font-weight: 700; text-align: center; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
    Exhibit A — Pricing and Rate Schedule
  </div>
  <p style="font-size: 10pt; text-align: justify;">
    All services shall be billed strictly in accordance with approved Purchase Orders under this Agreement. Standard invoice payment terms are <strong>${terms.paymentTerms}</strong>. Early payment discount terms: <strong>${terms.earlyPaymentDiscount45}</strong> for Net 45, and <strong>${terms.earlyPaymentDiscount30}</strong> for Net 30 payment terms.
  </p>
  ${terms.customClauses ? `
    <div style="margin-top: 14px; padding: 10px 14px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 4px;">
      <strong style="color: #92400e; font-size: 10pt; text-transform: uppercase;">Exhibit B — Special Conditions & Addendums</strong>
      <p style="margin: 6px 0 0 0; font-size: 9.5pt; white-space: pre-wrap;">${terms.customClauses}</p>
    </div>
  ` : ''}
</div>`
    ];
  };

  const handleLinkExisting = (doc) => {
    if (onLinkedSuccess) {
      onLinkedSuccess(doc);
    }
    Swal.fire({
      icon: 'success',
      title: 'Agreement Linked',
      text: `${doc.DocumentNumber} (${doc.Title}) has been linked to this step.`,
      timer: 1500,
      showConfirmButton: false
    });
    onClose();
  };

  const handleCreateDraft = async (e) => {
    e.preventDefault();
    const finalCompanyId = draftForm.counterpartyCompanyId || counterpartyCompanyId;
    if (!finalCompanyId) {
      Swal.fire({
        icon: 'warning',
        title: 'Counterparty Company Required',
        text: 'Please select a Counterparty Company for this agreement before drafting.'
      });
      return;
    }

    const selectedCompany = companies.find(c => String(c.CompanyId) === String(finalCompanyId));
    const effectiveCompanyName = counterpartyCompanyName || selectedCompany?.DisplayName || selectedCompany?.CompanyName || 'Counterparty';

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const payload = {
        documentTypeCode: draftForm.documentTypeCode,
        counterpartySide: draftForm.counterpartySide,
        governingEntityId: draftForm.governingEntityId || 4,
        counterpartyCompanyId: finalCompanyId,
        templateId: draftForm.templateId ? parseInt(draftForm.templateId, 10) : null,
        title: draftForm.title || `${draftForm.documentTypeCode} - ${effectiveCompanyName}`,
        effectiveFromDate: draftForm.effectiveFromDate,
        effectiveToDate: draftForm.effectiveToDate || null,
        autoRenew: draftForm.autoRenew,
        renewalTermMonths: draftForm.renewalTermMonths ? parseInt(draftForm.renewalTermMonths, 10) : null,
        renewalNoticeDays: draftForm.renewalNoticeDays ? parseInt(draftForm.renewalNoticeDays, 10) : null,
        notes: draftForm.notes,
        poDetail: draftForm.documentTypeCode === 'PO' ? {
          consultantName: draftForm.consultantName,
          endClientName: draftForm.endClientName,
          workLocation: draftForm.workLocation,
          startDate: draftForm.startDate,
          durationText: draftForm.durationText,
          billRate: draftForm.billRate,
          billRateType: draftForm.billRateType,
          currencyCode: draftForm.currencyCode,
          paymentTermDays: draftForm.paymentTermDays,
          vmsFeePct: draftForm.vmsFeePct
        } : null,
        signers: [
          { partySide: 'GOVERNING', signerName: 'Senthil Marimuthu', signerEmail: 'kumar@prophecytechs.com', signerTitle: 'Chief Executive Officer', isRequired: true },
          { 
            partySide: 'COUNTERPARTY', 
            signerName: (() => {
              const defaultCp = (companyContacts || []).find(c => c.IsSigningAuthority && c.Email) || (companyContacts || [])[0];
              return defaultCp ? `${defaultCp.FirstName || ''} ${defaultCp.LastName || ''}`.trim() : (draftForm.counterpartySignerName || '');
            })(),
            signerEmail: (() => {
              const defaultCp = (companyContacts || []).find(c => c.IsSigningAuthority && c.Email) || (companyContacts || [])[0];
              return defaultCp ? defaultCp.Email : (draftForm.counterpartySignerEmail || '');
            })(),
            signerTitle: (() => {
              const defaultCp = (companyContacts || []).find(c => c.IsSigningAuthority && c.Email) || (companyContacts || [])[0];
              return defaultCp ? (defaultCp.JobTitle || (defaultCp.IsSigningAuthority ? 'Authorized Representative' : '')) : (draftForm.counterpartySignerTitle || '');
            })(),
            signerContactId: (() => {
              const defaultCp = (companyContacts || []).find(c => c.IsSigningAuthority && c.Email) || (companyContacts || [])[0];
              return defaultCp ? defaultCp.ContactId : null;
            })(),
            isRequired: true 
          }
        ]
      };

      const res = await axios.post(`${BASE_URL}/api/crm/commercial-documents`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        const createdDoc = res.data.data;
        Swal.fire({
          icon: 'success',
          title: 'Draft Created',
          text: `Document ${createdDoc.DocumentNumber} created successfully in DRAFT.`
        });
        if (onCreatedSuccess) onCreatedSuccess(createdDoc);
        if (onLinkedSuccess) onLinkedSuccess(createdDoc);
        onClose();
      }
    } catch (err) {
      console.error('Error creating draft:', err);
      Swal.fire({
        icon: 'error',
        title: 'Draft Creation Failed',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendForSignature = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BASE_URL}/api/crm/commercial-documents/${viewDoc.CommercialDocumentId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Sent for Signature!',
          text: `Document ${viewDoc.DocumentNumber} is now active for signing.`,
          timer: 2000,
          showConfirmButton: false
        });
        fetchDocForView(viewDoc.CommercialDocumentId);
      }
    } catch (err) {
      console.error('Error sending document:', err);
      const isAuthorityError = err.response?.status === 422 && (
        err.response?.data?.message?.includes('Signing Authority') || 
        (err.response?.data?.details && err.response.data.details.some(d => d.includes('Signing Authority')))
      );
      if (isAuthorityError) {
        Swal.fire({
          icon: 'warning',
          title: 'Signing Authority Required',
          html: `<div style="text-align: left; font-size: 13.5px; color: #374151;">
            <p><b>Cannot dispatch document to Counterparty Portal:</b></p>
            <p style="color: #b91c1c; background: #fef2f2; padding: 10px; border-radius: 6px; border: 1px solid #fca5a5;">
              ${err.response?.data?.message || err.response?.data?.details?.[0]}
            </p>
            <p style="margin-top: 10px; color: #4b5563;">
              💡 <b>Next Step:</b> Open this Company in CRM, go to the <b>Contacts</b> tab, and click <b>+ Signing Auth</b> for the designated signer.
            </p>
          </div>`,
          confirmButtonColor: '#0284c7',
          confirmButtonText: 'Understood'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Send Failed',
          text: err.response?.data?.message || (err.response?.data?.details && err.response.data.details.join(', ')) || err.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BASE_URL}/api/crm/commercial-documents/${viewDoc.CommercialDocumentId}/file`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'File Attached',
          text: `Attached file "${file.name}" to agreement.`,
          timer: 1800,
          showConfirmButton: false
        });
        fetchDocForView(viewDoc.CommercialDocumentId);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleSignConfirm = async () => {
    const signerIdToUse = selectedSignerId || (viewDoc.signers || []).find(s => s.StatusCode !== 'SIGNED')?.SignerId;
    if (!signerIdToUse) {
      Swal.fire({ icon: 'info', title: 'Already Signed', text: 'All signers have already signed this document.' });
      return;
    }
    if (!typedSignName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Signature Required', text: 'Please type your full name to sign.' });
      return;
    }
    try {
      setIsSigning(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BASE_URL}/api/crm/commercial-documents/${viewDoc.CommercialDocumentId}/signers/${signerIdToUse}/sign`,
        { typedName: typedSignName.trim() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Document Signed!',
          text: `Signature successfully recorded for ${typedSignName.trim()}. Document updated.`
        });
        fetchDocForView(viewDoc.CommercialDocumentId);
      }
    } catch (e) {
      console.error('Signing error:', e);
      Swal.fire({ icon: 'error', title: 'Signing Failed', text: e.response?.data?.message || e.message });
    } finally {
      setIsSigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        width: viewDoc ? '96vw' : '100%',
        maxWidth: viewDoc ? '1420px' : '780px',
        height: viewDoc ? '94vh' : 'auto',
        maxHeight: '94vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b5bfd 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.18)', padding: '8px', borderRadius: '8px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LuFileText size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 650 }}>
                {viewDoc ? `${viewDoc.DocumentNumber} — ${viewDoc.Title}` : `${documentTypeCode} Management & Linking`}
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#dbeafe' }}>
                Governing: <strong>{governingEntityName}</strong> | Counterparty: <strong>{counterpartyCompanyName || 'Partner'}</strong> ({counterpartySide})
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {viewDoc && (
              <button
                type="button"
                onClick={() => {
                  setViewDoc(null);
                  setTemplatePreview(null);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  color: '#ffffff',
                  borderRadius: '6px',
                  padding: '5px 12px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                ← Back to List
              </button>
            )}
            <button
              onClick={() => {
                setTemplatePreview(null);
                onClose();
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#dbeafe',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '6px'
              }}
            >
              <LuX size={20} />
            </button>
          </div>
        </div>

        {/* View / Sign Full Document Mode */}
        {viewDoc ? (
          <div style={{ display: 'flex', flex: 1, minHeight: '560px', overflow: 'hidden' }}>
            {/* Left: Complete Document Preview */}
            <div style={{ flex: 1, minWidth: 0, borderRight: '1px solid #e2e8f0', background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '8px 16px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#3b5bfd', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <LuFileText size={16} color="#3b5bfd" /> Complete Agreement Document ({viewDoc.StatusCode})
                  </span>
                  <span style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    backgroundColor: '#e2e8f0',
                    color: '#334155',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}>
                    Ref: {viewDoc.DocumentNumber}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Zoom Controls */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#ffffff',
                    padding: '2px 6px',
                    borderRadius: '5px',
                    border: '1px solid #cbd5e1'
                  }}>
                    <button
                      type="button"
                      onClick={() => setZoomLevel(z => Math.max(70, z - 10))}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', padding: '0 4px', color: '#475569' }}
                      title="Zoom Out"
                    >
                      -
                    </button>
                    <span style={{ fontSize: '0.72rem', fontWeight: 650, color: '#334155', minWidth: '34px', textAlign: 'center' }}>
                      {zoomLevel}%
                    </span>
                    <button
                      type="button"
                      onClick={() => setZoomLevel(z => Math.min(140, z + 10))}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', padding: '0 4px', color: '#475569' }}
                      title="Zoom In"
                    >
                      +
                    </button>
                  </div>

                  {/* Link to Step Button */}
                  <button
                    type="button"
                    onClick={() => handleLinkExisting(viewDoc)}
                    style={{
                      background: '#3b5bfd',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 650,
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <LuCheck size={13} /> Link to Step
                  </button>

                  {/* Edit Agreement Button */}
                  {viewDoc.StatusCode !== 'CANCELLED' && (
                    <button
                      type="button"
                      onClick={() => setIsEditingTerms(!isEditingTerms)}
                      style={{
                        background: isEditingTerms ? '#dbeafe' : '#ffffff',
                        border: '1px solid #3b5bfd',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 650,
                        color: '#3b5bfd',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                      }}
                    >
                      {isEditingTerms ? <><LuEye size={13} /> View Agreement</> : <><LuPencil size={13} /> Edit Terms & Clauses</>}
                    </button>
                  )}

                  {/* Print / Save PDF Button */}
                  <button
                    type="button"
                    onClick={() => window.print()}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '4px',
                      padding: '4px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <LuPrinter size={13} /> Print / Save
                  </button>

                  {/* Hidden file input to attach custom document */}
                  <input
                    type="file"
                    id="comm-doc-attach-file"
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx"
                    onChange={handleUploadDocFile}
                  />
                  {viewDoc.StatusCode === 'DRAFT' && (
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById('comm-doc-attach-file');
                        if (el) el.click();
                      }}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#475569',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <LuPlus size={12} /> Attach File
                    </button>
                  )}
                  {(viewDoc.CurrentFileDocumentId || viewDoc.ExecutedFileDocumentId) && (
                    <a
                      href={`${BASE_URL}/api/crm/commercial-documents/${viewDoc.CommercialDocumentId}/file`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#0284c7', textDecoration: 'none' }}
                    >
                      <LuExternalLink size={14} /> Open Tab
                    </a>
                  )}
                </div>
              </div>

              {/* Mode A: In-Place Agreement Terms Editor (Only active when isEditingTerms = true in DRAFT) */}
              {isEditingTerms ? (
                <div style={{ flex: 1, overflowY: 'auto', padding: '22px 26px', backgroundColor: '#f8fafc' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>
                        Edit Agreement Terms & Variables
                      </h3>
                      <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                        Customize legal terms, dates, and signers. All updates reflect dynamically on the MSA.
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setIsEditingTerms(false)}
                        style={{ padding: '6px 12px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveAgreementChanges}
                        disabled={loading}
                        style={{ padding: '6px 14px', background: '#0284c7', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 650, color: '#ffffff', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <LuSave size={14} /> {loading ? 'Saving...' : 'Save Agreement Changes'}
                      </button>
                    </div>
                  </div>

                  {/* Section 0: Agreement Document Title & Reference */}
                  <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#0284c7', marginBottom: '12px' }}>
                      Agreement Title & Reference
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Agreement Title *
                      </label>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="e.g. MSA — TCS (US)"
                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Section 1: Subcontractor (Provider) Details */}
                  <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#0284c7', marginBottom: '12px' }}>
                      1. Subcontractor (Provider) Legal Information
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Provider Legal Name *
                        </label>
                        <input
                          type="text"
                          value={editForm.providerLegalName}
                          onChange={(e) => setEditForm(prev => ({ ...prev, providerLegalName: e.target.value }))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Federal Tax ID / EIN *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={9}
                          value={editForm.providerTaxId}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                            setEditForm(prev => ({ ...prev, providerTaxId: val }));
                          }}
                          placeholder="e.g. 123456789 (9 digits)"
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: `1px solid ${editForm.providerTaxId && editForm.providerTaxId.length !== 9 ? '#ef4444' : '#cbd5e1'}`,
                            fontSize: '0.85rem',
                            boxSizing: 'border-box'
                          }}
                        />
                        {editForm.providerTaxId && editForm.providerTaxId.length !== 9 && (
                          <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '3px' }}>
                            Must be exactly 9 digits ({editForm.providerTaxId.length}/9)
                          </div>
                        )}
                      </div>
                      <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Provider Legal & Notice Address *
                        </label>
                        <input
                          type="text"
                          value={editForm.providerAddress}
                          onChange={(e) => setEditForm(prev => ({ ...prev, providerAddress: e.target.value }))}
                          placeholder="e.g. Street Address, Suite / Unit, City, State ZIP"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Term, Duration & Notice */}
                  <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#0284c7', marginBottom: '12px' }}>
                      2. Term, Operational Limits & Retention
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Start Date *
                        </label>
                        <input
                          type="date"
                          value={editForm.effectiveFromDate}
                          onChange={(e) => setEditForm(prev => ({ ...prev, effectiveFromDate: e.target.value }))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          End Date *
                        </label>
                        <input
                          type="date"
                          value={editForm.effectiveToDate}
                          onChange={(e) => setEditForm(prev => ({ ...prev, effectiveToDate: e.target.value }))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Initial Term (Years)
                        </label>
                        <select
                          value={editForm.termYears}
                          onChange={(e) => setEditForm(prev => ({ ...prev, termYears: e.target.value }))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        >
                          <option value="1">1 Year</option>
                          <option value="2">2 Years</option>
                          <option value="3">3 Years (Standard)</option>
                          <option value="5">5 Years</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Notice of Termination (Days)
                        </label>
                        <input
                          type="number"
                          value={editForm.renewalNoticeDays}
                          onChange={(e) => setEditForm(prev => ({ ...prev, renewalNoticeDays: e.target.value }))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Max Subcontracting Cap %
                        </label>
                        <input
                          type="text"
                          value={editForm.subcontractingCapPct}
                          onChange={(e) => setEditForm(prev => ({ ...prev, subcontractingCapPct: e.target.value }))}
                          placeholder="40"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Books Retention (Years)
                        </label>
                        <input
                          type="text"
                          value={editForm.booksRetentionYears}
                          onChange={(e) => setEditForm(prev => ({ ...prev, booksRetentionYears: e.target.value }))}
                          placeholder="7"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Non-Solicitation Fee ($)
                        </label>
                        <input
                          type="text"
                          value={editForm.nonSolicitationFee}
                          onChange={(e) => setEditForm(prev => ({ ...prev, nonSolicitationFee: e.target.value }))}
                          placeholder="3,500"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Invoicing & Payment Terms */}
                  <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#0284c7', marginBottom: '12px' }}>
                      3. Invoicing, Payments & Early Discounts (Article 8)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Standard Payment Terms
                        </label>
                        <select
                          value={editForm.paymentTerms}
                          onChange={(e) => setEditForm(prev => ({ ...prev, paymentTerms: e.target.value }))}
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        >
                          <option value="Net 60">Net 60 (Standard)</option>
                          <option value="Net 45">Net 45</option>
                          <option value="Net 30">Net 30</option>
                          <option value="Net 15">Net 15</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Early Discount (Net 45)
                        </label>
                        <input
                          type="text"
                          value={editForm.earlyPaymentDiscount45}
                          onChange={(e) => setEditForm(prev => ({ ...prev, earlyPaymentDiscount45: e.target.value }))}
                          placeholder="3%"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Early Discount (Net 30)
                        </label>
                        <input
                          type="text"
                          value={editForm.earlyPaymentDiscount30}
                          onChange={(e) => setEditForm(prev => ({ ...prev, earlyPaymentDiscount30: e.target.value }))}
                          placeholder="5%"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Signers & Representatives */}
                  <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#0284c7', marginBottom: '12px' }}>
                      4. Signers & Execution Representatives
                    </div>
                    {companyContacts.length > 0 && (
                      <div style={{ marginBottom: '14px', padding: '10px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#3b5bfd', marginBottom: '4px' }}>
                          Select from Company Contacts (Auto-fills Signer Info)
                        </label>
                        <select
                          style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.84rem', background: '#ffffff' }}
                          onChange={(e) => {
                            const c = companyContacts.find(x => String(x.ContactId) === String(e.target.value));
                            if (c) {
                              setEditForm(prev => ({
                                ...prev,
                                counterpartySignerName: `${c.FirstName || ''} ${c.LastName || ''}`.trim(),
                                counterpartySignerTitle: c.JobTitle || (c.IsSigningAuthority ? 'Authorized Representative' : ''),
                                counterpartySignerEmail: c.Email || ''
                              }));
                            }
                          }}
                        >
                          <option value="">-- Choose an Authorized Signer --</option>
                          {companyContacts.map(c => (
                            <option key={c.ContactId} value={c.ContactId}>
                              {c.FirstName} {c.LastName} {c.IsSigningAuthority ? '★ SIGNING AUTHORITY' : ''} ({c.Email || 'No email'})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Counterparty Signer Name *
                        </label>
                        <input
                          type="text"
                          value={editForm.counterpartySignerName}
                          onChange={(e) => setEditForm(prev => ({ ...prev, counterpartySignerName: e.target.value }))}
                          placeholder="Enter counterparty signer name"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Counterparty Signer Title
                        </label>
                        <input
                          type="text"
                          value={editForm.counterpartySignerTitle}
                          onChange={(e) => setEditForm(prev => ({ ...prev, counterpartySignerTitle: e.target.value }))}
                          placeholder="e.g. Authorized Representative / VP"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Counterparty Signer Email *
                        </label>
                        <input
                          type="email"
                          value={editForm.counterpartySignerEmail}
                          onChange={(e) => setEditForm(prev => ({ ...prev, counterpartySignerEmail: e.target.value }))}
                          placeholder="e.g. signer@counterparty.com"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Governing Signer (Prophecy Consulting Inc)
                        </label>
                        <input
                          type="text"
                          disabled
                          value="Senthil Marimuthu (Chief Executive Officer)"
                          style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box', backgroundColor: '#f1f5f9', color: '#64748b' }}
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: '12px', padding: '10px 14px', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe', fontSize: '0.79rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>💡 <b>Signer Requirement:</b> The designated Counterparty Signer must be granted <b>Signing Authority</b> in the company's CRM Contacts to log in and sign via the Counterparty Portal.</span>
                    </div>
                  </div>

                  {/* Section 5: Custom Addendums / Special Notes */}
                  <div style={{ background: '#ffffff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', color: '#3b5bfd', marginBottom: '12px' }}>
                      5. Special Addendums & Custom Clauses
                    </div>
                    <textarea
                      rows={3}
                      value={editForm.customClauses}
                      onChange={(e) => setEditForm(prev => ({ ...prev, customClauses: e.target.value }))}
                      placeholder="Enter any custom clauses, additional exhibits, or special contractual requirements here..."
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={() => setIsEditingTerms(false)}
                      style={{ padding: '8px 16px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveAgreementChanges}
                      disabled={loading}
                      style={{ padding: '8px 20px', background: '#3b5bfd', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 650, color: '#ffffff', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <LuSave size={15} /> {loading ? 'Saving Changes...' : 'Save Agreement Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Authentic Word DOCX Multi-Page Document Layout */
                (() => {
                  const terms = getActiveTerms(viewDoc);
                  const rawPages = (templatePreview?.pages && templatePreview.pages.length > 0)
                    ? (templatePreview.pages.length === 1 ? getDocPages(templatePreview.pages[0], templatePreview) : templatePreview.pages)
                    : (templatePreview?.html ? getDocPages(templatePreview.html, templatePreview) : getFallbackDocPages(terms, viewDoc));

                  return (
                    <div style={{
                      flex: 1,
                      overflowY: 'auto',
                      padding: '32px 20px',
                      backgroundColor: '#f1f5f9',
                      boxSizing: 'border-box'
                    }}>
                      <style>{`
                        .template-document-paper {
                          background-color: #ffffff !important;
                          box-sizing: border-box !important;
                          font-family: 'Cambria', Georgia, 'Times New Roman', serif !important;
                          color: #111827 !important;
                        }
                        .template-document-paper * {
                          box-sizing: border-box;
                        }
                        .template-document-paper .doc-top-bar {
                          height: 4px !important;
                          background-color: #8E425F !important;
                          width: 100% !important;
                          margin-bottom: 12px !important;
                          border-radius: 2px !important;
                          display: block !important;
                        }
                        .template-document-paper .doc-title-heading {
                          font-size: 20pt !important;
                          font-weight: 700 !important;
                          color: #0f172a !important;
                          text-align: center !important;
                          margin: 18px 0 16px 0 !important;
                          letter-spacing: 0.8px !important;
                          text-transform: uppercase !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper .doc-section-heading {
                          font-size: 14pt !important;
                          font-weight: 700 !important;
                          color: #0f172a !important;
                          text-align: center !important;
                          margin: 18px 0 12px 0 !important;
                          letter-spacing: 0.6px !important;
                          text-transform: uppercase !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper p {
                          margin-bottom: 12px !important;
                          color: #111827 !important;
                          line-height: 1.65 !important;
                          text-align: justify !important;
                          text-justify: inter-word !important;
                          font-size: 11pt !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper ol {
                          padding-left: 28px !important;
                          margin: 10px 0 16px 0 !important;
                          color: #111827 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper ul {
                          padding-left: 28px !important;
                          margin: 8px 0 14px 0 !important;
                          color: #111827 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper li {
                          margin-bottom: 11px !important;
                          color: #111827 !important;
                          line-height: 1.65 !important;
                          text-align: justify !important;
                          text-justify: inter-word !important;
                          font-size: 11pt !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper li strong,
                        .template-document-paper li u,
                        .template-document-paper strong {
                          color: #0f172a !important;
                        }
                        .template-document-paper table {
                          width: 100% !important;
                          border-collapse: collapse !important;
                          margin: 20px 0 !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper table td,
                        .template-document-paper table th {
                          border: 1px solid #94a3b8 !important;
                          padding: 10px 14px !important;
                          vertical-align: top !important;
                          font-size: 10.5pt !important;
                          color: #0f172a !important;
                          line-height: 1.5 !important;
                        }
                        .template-document-paper table tr td:first-child {
                          font-weight: 600 !important;
                          background-color: #f8fafc !important;
                          width: 32% !important;
                        }
                        .template-document-paper .doc-footer-banner {
                          margin-top: 50px !important;
                          width: 100% !important;
                          border-radius: 2px !important;
                          overflow: hidden !important;
                          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
                          display: block !important;
                        }
                        .template-document-paper .doc-footer-accent {
                          height: 4px !important;
                          background-color: #8E425F !important;
                          width: 100% !important;
                          display: block !important;
                        }
                        .template-document-paper .doc-footer-body {
                          background-color: #01497C !important;
                          padding: 14px 24px !important;
                          display: flex !important;
                          align-items: center !important;
                          justify-content: space-between !important;
                          gap: 16px !important;
                          color: #ffffff !important;
                        }
                        .template-document-paper .doc-footer-text {
                          text-align: right !important;
                          font-size: 11px !important;
                          line-height: 1.45 !important;
                          color: #ffffff !important;
                          font-family: 'Cambria', Georgia, serif !important;
                        }
                        .template-document-paper .doc-footer-text a {
                          color: #ffffff !important;
                          text-decoration: underline !important;
                        }
                        @media print {
                          body * {
                            visibility: hidden;
                          }
                          .template-document-paper, .template-document-paper * {
                            visibility: visible;
                          }
                          .template-document-paper {
                            position: relative !important;
                            left: 0;
                            top: 0;
                            width: 100% !important;
                            max-width: 100% !important;
                            box-shadow: none !important;
                            border: none !important;
                            padding: 20px 40px !important;
                            page-break-after: always !important;
                            break-after: page !important;
                            page-break-inside: avoid !important;
                          }
                        }
                      `}</style>

                      {/* PO Work Schedule Callout if document is PO */}
                      {viewDoc.DocumentTypeCode === 'PO' && viewDoc.poDetail && (
                        <div style={{
                          maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                          margin: '0 auto 18px',
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: '6px',
                          padding: '14px 18px'
                        }}>
                          <h4 style={{ margin: '0 0 10px', fontSize: '0.92rem', color: '#1d4ed8', fontWeight: 700 }}>
                            Purchase Order Schedule Details
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.85rem' }}>
                            <div><strong>Consultant:</strong> {viewDoc.poDetail.ConsultantName || 'Assigned Consultant'}</div>
                            <div><strong>End Client:</strong> {viewDoc.poDetail.EndClientName || 'Direct Engagement'}</div>
                            <div><strong>{(viewDoc.CounterpartySide === 'SUPPLIER' || counterpartySide === 'SUPPLIER') ? 'Supplier Rate:' : 'Bill Rate:'}</strong> ${Number(viewDoc.poDetail.BillRate || 0).toFixed(2)} / {viewDoc.poDetail.BillRateType || 'HOURLY'}</div>
                            <div><strong>Payment Terms:</strong> Net {viewDoc.poDetail.PaymentTermDays || 60} Days</div>
                            <div><strong>Work Location:</strong> {viewDoc.poDetail.WorkLocation || 'Remote / Client Site'}</div>
                            <div><strong>Duration:</strong> {viewDoc.poDetail.DurationText || 'As agreed'}</div>
                          </div>
                        </div>
                      )}

                      {previewLoading && (
                        <div style={{ padding: '40px', color: '#64748b', textAlign: 'center' }}>
                          <LuLayers size={32} style={{ animation: 'spin 2s linear infinite', margin: '0 auto 10px auto' }} />
                          <p style={{ margin: 0, fontSize: '0.88rem' }}>Loading formal document sheets...</p>
                        </div>
                      )}

                      {/* Render All Document Pages */}
                      {rawPages.map((pageHtml, pageIdx) => {
                        const processedHtml = interpolateDocData(formatDocumentHtml(pageHtml), terms, viewDoc, pageIdx, rawPages.length);

                        return (
                          <div key={pageIdx} style={{ width: '100%', marginBottom: pageIdx === rawPages.length - 1 ? '40px' : '0' }}>
                            <div
                              className="template-document-paper"
                              style={{
                                backgroundColor: '#ffffff',
                                width: '100%',
                                maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                                margin: '0 auto',
                                padding: `${Math.round(48 * (zoomLevel / 100))}px ${Math.round(56 * (zoomLevel / 100))}px`,
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                color: '#111827',
                                fontSize: `${14.5 * (zoomLevel / 100)}px`,
                                lineHeight: 1.65,
                                fontFamily: "'Cambria', Georgia, 'Times New Roman', serif",
                                minHeight: '750px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                boxSizing: 'border-box'
                              }}
                            >
                              <div>
                                {/* Word Template Header Accent Bar */}
                                <div className="doc-top-bar" />

                                {/* Running Header Bar with Page Number indicator */}
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: '0.78rem',
                                  color: '#64748b',
                                  marginBottom: '18px',
                                  fontFamily: "'Cambria', Georgia, serif"
                                }}>
                                  <span style={{ fontWeight: 600, letterSpacing: '0.2px' }}>
                                    {viewDoc.DocumentTypeCode === 'PO' ? 'VENDOR WORK ORDER' : (viewDoc.DocumentTypeCode === 'SOW' ? 'STATEMENT OF WORK' : 'MASTER SERVICES AGREEMENT')}
                                  </span>
                                  <span style={{
                                    fontWeight: 700,
                                    backgroundColor: '#f1f5f9',
                                    color: '#475569',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    border: '1px solid #e2e8f0',
                                    fontSize: '0.74rem'
                                  }}>
                                    Page {pageIdx + 1} of {rawPages.length}
                                  </span>
                                </div>

                                {/* Word Template Document Content for this page */}
                                <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
                              </div>

                              {/* Word Template Footer Banner for each page */}
                              <div className="doc-footer-banner">
                                <div className="doc-footer-accent" />
                                <div className="doc-footer-body">
                                  <div className="doc-footer-logo">
                                    <img
                                      src="/prophecy_logo.png"
                                      alt="Prophecy Technologies"
                                      style={{ height: `${Math.round(34 * (zoomLevel / 100))}px`, objectFit: 'contain', display: 'block' }}
                                    />
                                  </div>
                                  <div className="doc-footer-text">
                                    <div style={{ fontWeight: 700, letterSpacing: '0.2px' }}>
                                      Prophecy Consulting Inc <span style={{ opacity: 0.7, fontWeight: 400 }}>|</span> <a href="https://www.prophecytechs.com" target="_blank" rel="noopener noreferrer">www.prophecytechs.com</a>
                                    </div>
                                    <div>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</div>
                                    <div>
                                      <a href="mailto:onboarding@prophecytechs.com">onboarding@prophecytechs.com</a> <span style={{ opacity: 0.7 }}>|</span> +1(551) 689-4006
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Visual Page Break Separator Between Pages */}
                            {pageIdx < rawPages.length - 1 && (
                              <div style={{
                                maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                                margin: '24px auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px'
                              }}>
                                <div style={{ flex: 1, height: '1px', background: '#cbd5e1', borderStyle: 'dashed' }} />
                                <span style={{
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  color: '#64748b',
                                  backgroundColor: '#e2e8f0',
                                  padding: '3px 12px',
                                  borderRadius: '12px',
                                  letterSpacing: '0.5px',
                                  textTransform: 'uppercase'
                                }}>
                                  Page {pageIdx + 1} of {rawPages.length} &bull; Page Break
                                </span>
                                <div style={{ flex: 1, height: '1px', background: '#cbd5e1', borderStyle: 'dashed' }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>

            {/* Right: Signers & In-System Typed Signing Box */}
            <div style={{ width: '340px', flex: '0 0 340px', padding: '18px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', background: '#ffffff' }}>
              <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, marginBottom: '8px' }}>
                  Signers Status
                </div>
                {(viewDoc.signers || []).map((s, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ flex: 1, marginRight: '8px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>
                        {s.SignerName || (s.PartySide === 'COUNTERPARTY' ? 'Pending Assignment' : 'Signer')}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                        {s.PartySide} &bull; {s.SignerEmail || (s.PartySide === 'COUNTERPARTY' ? 'Pending Email' : 'No email')}
                      </div>
                      {s.PartySide === 'COUNTERPARTY' && viewDoc.StatusCode === 'DRAFT' && companyContacts.length > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <select
                            style={{
                              width: '100%',
                              fontSize: '0.76rem',
                              padding: '4px 6px',
                              borderRadius: '4px',
                              border: '1px solid #94a3b8',
                              background: '#f8fafc',
                              color: '#1e293b'
                            }}
                            value={s.SignerContactId || ''}
                            onChange={async (e) => {
                              const contactId = e.target.value;
                              const contact = companyContacts.find(c => String(c.ContactId) === String(contactId));
                              if (!contact) return;
                              try {
                                const token = localStorage.getItem('token');
                                const patchPayload = {
                                  signers: [
                                    {
                                      SignerId: s.SignerId,
                                      SignerName: `${contact.FirstName || ''} ${contact.LastName || ''}`.trim(),
                                      SignerEmail: contact.Email || '',
                                      SignerTitle: contact.JobTitle || (contact.IsSigningAuthority ? 'Authorized Representative' : ''),
                                      SignerContactId: contact.ContactId
                                    }
                                  ]
                                };
                                await axios.patch(`${BASE_URL}/api/crm/commercial-documents/${viewDoc.CommercialDocumentId}`, patchPayload, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                fetchDocForView(viewDoc.CommercialDocumentId);
                              } catch (err) {
                                console.error('Error assigning signer:', err);
                              }
                            }}
                          >
                            <option value="">-- Assign Signer --</option>
                            {companyContacts.map(c => (
                              <option key={c.ContactId} value={c.ContactId}>
                                {c.FirstName} {c.LastName} {c.IsSigningAuthority ? '★ SIGNING AUTH' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      backgroundColor: s.StatusCode === 'SIGNED' ? '#dcfce7' : (s.StatusCode === 'VIEWED' ? '#e0f2fe' : '#f1f5f9'),
                      color: s.StatusCode === 'SIGNED' ? '#1d4ed8' : (s.StatusCode === 'VIEWED' ? '#0369a1' : '#475569'),
                      flexShrink: 0
                    }}>
                      {s.StatusCode}
                    </span>
                  </div>
                ))}
              </div>

              {/* Draft Ready Action Box */}
              {viewDoc.StatusCode === 'DRAFT' && (
                <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#1e40af', fontWeight: 700, fontSize: '0.9rem' }}>
                    <LuFileText size={18} /> Draft Agreement Ready
                  </div>
                  <p style={{ fontSize: '0.82rem', color: '#334155', margin: '0 0 14px', lineHeight: 1.5 }}>
                    Review the formal agreement terms and signers on the left. Once ready, click <strong>Send for Signature</strong> to activate the signing workflow for all parties.
                  </p>
                  <button
                    type="button"
                    onClick={handleSendForSignature}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: '#0284c7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 650,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '0.92rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <LuCheck size={16} /> Send for Signature
                  </button>
                </div>
              )}

              {/* Signing Action Box */}
              {['SENT_FOR_SIGNATURE', 'PARTIALLY_SIGNED'].includes(viewDoc.StatusCode) && (
                <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#1d4ed8', fontWeight: 700, fontSize: '0.9rem' }}>
                    <LuShieldCheck size={18} /> In-System Typed Name Signing
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#374151', margin: '0 0 12px' }}>
                    By typing your name below, you confirm and execute your signature with a legally binding digital audit stamp (typed name, UTC timestamp, IP address).
                  </p>

                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Sign as:
                  </label>
                  <select
                    value={selectedSignerId || ''}
                    onChange={(e) => {
                      const sId = Number(e.target.value);
                      setSelectedSignerId(sId);
                      const s = (viewDoc.signers || []).find(x => x.SignerId === sId);
                      if (s) setTypedSignName(s.SignerName || '');
                    }}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #bfdbfe', fontSize: '0.88rem', marginBottom: '10px', boxSizing: 'border-box', backgroundColor: '#ffffff' }}
                  >
                    {(viewDoc.signers || []).map(s => (
                      <option key={s.SignerId} value={s.SignerId} disabled={s.StatusCode === 'SIGNED'}>
                        {s.SignerName} ({s.PartySide}) {s.StatusCode === 'SIGNED' ? '— Signed ✓' : '— Ready to Sign'}
                      </option>
                    ))}
                  </select>

                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Type Your Full Legal Name:
                  </label>
                  <input
                    type="text"
                    value={typedSignName}
                    onChange={(e) => setTypedSignName(e.target.value)}
                    placeholder="e.g. Senthil Marimuthu"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #bfdbfe',
                      fontSize: '1rem',
                      fontFamily: 'serif',
                      marginBottom: '12px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSignConfirm}
                    disabled={isSigning || !typedSignName.trim()}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: '#3b5bfd',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 650,
                      cursor: isSigning || !typedSignName.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '0.92rem'
                    }}
                  >
                    {isSigning ? 'Recording Signature...' : 'Confirm & Sign Document ✓'}
                  </button>
                </div>
              )}

              {/* Fully Executed State */}
              {viewDoc.StatusCode === 'EXECUTED' && (
                <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div style={{ color: '#1d4ed8', fontWeight: 700, fontSize: '1rem', marginBottom: '6px' }}>
                    ✓ Fully Executed & Active
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#1d4ed8', lineHeight: 1.5 }}>
                    All required parties have executed their signatures. This agreement is legally binding and active.
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Link or Create Tabs Mode */
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 20px' }}>
              <button
                type="button"
                onClick={() => setActiveTab('LINK')}
                style={{
                  padding: '12px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === 'LINK' ? '3px solid #3b5bfd' : '3px solid transparent',
                  color: activeTab === 'LINK' ? '#3b5bfd' : '#64748b',
                  fontWeight: 650,
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                1. Existing Agreements ({existingDocs.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('DRAFT')}
                style={{
                  padding: '12px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === 'DRAFT' ? '3px solid #3b5bfd' : '3px solid transparent',
                  color: activeTab === 'DRAFT' ? '#3b5bfd' : '#64748b',
                  fontWeight: 650,
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                2. Draft New {documentTypeCode}
              </button>
            </div>

            {/* Content Body */}
            <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
              {activeTab === 'LINK' && (
                <div>
                  <div style={{ marginBottom: '14px', fontSize: '0.85rem', color: '#475569' }}>
                    Below are the commercial agreements registered for <strong>{counterpartyCompanyName || 'this company'}</strong> ({counterpartySide || 'SUPPLIER'}):
                  </div>

                  {loading ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Checking agreements...</div>
                  ) : existingDocs.length === 0 ? (
                    <div style={{
                      padding: '36px 20px',
                      textAlign: 'center',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px dashed #cbd5e1'
                    }}>
                      <LuFileText size={36} color="#94a3b8" style={{ marginBottom: '8px' }} />
                      <div style={{ fontWeight: 600, color: '#334155' }}>No agreements found</div>
                      <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '4px 0 16px' }}>
                        No {documentTypeCode} exists for {counterpartyCompanyName || 'this company'} ({counterpartySide || 'SUPPLIER'}) yet.
                      </p>
                      <button
                        type="button"
                        onClick={() => setActiveTab('DRAFT')}
                        style={{
                          background: '#3b5bfd',
                          color: '#fff',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <LuPlus size={15} /> Draft New {documentTypeCode}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {existingDocs.map((doc) => (
                        <div
                          key={doc.CommercialDocumentId}
                          style={{
                            padding: '14px 18px',
                            background: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>
                                {doc.DocumentNumber}
                              </span>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                backgroundColor: doc.StatusCode === 'ACTIVE' || doc.StatusCode === 'EXECUTED' ? '#dcfce7' : (doc.StatusCode === 'DRAFT' ? '#fef3c7' : (doc.StatusCode === 'PARTIALLY_SIGNED' ? '#e0f2fe' : '#f1f5f9')),
                                color: doc.StatusCode === 'ACTIVE' || doc.StatusCode === 'EXECUTED' ? '#1d4ed8' : (doc.StatusCode === 'DRAFT' ? '#92400e' : (doc.StatusCode === 'PARTIALLY_SIGNED' ? '#0369a1' : '#475569'))
                              }}>
                                {doc.StatusCode}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {doc.CounterpartySide}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#334155' }}>{doc.Title}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                              Effective: {doc.EffectiveFromDate ? doc.EffectiveFromDate.substring(0, 10) : 'N/A'} to {doc.EffectiveToDate ? doc.EffectiveToDate.substring(0, 10) : 'Auto-renew'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={() => fetchDocForView(doc.CommercialDocumentId)}
                              style={{
                                background: '#f1f5f9',
                                border: '1px solid #cbd5e1',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                color: '#334155',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <LuEye size={14} /> View
                            </button>
                            <button
                              type="button"
                              onClick={() => handleLinkExisting(doc)}
                              style={{
                                background: '#3b5bfd',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                fontSize: '0.82rem',
                                fontWeight: 650,
                                color: '#ffffff',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <LuCheck size={14} /> Link to Step
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'DRAFT' && (
                <form onSubmit={handleCreateDraft}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div style={{ gridColumn: '1 / -1', position: 'relative' }} ref={companyDropdownRef}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Counterparty Company *
                      </label>

                      {/* Hidden input for HTML form validation */}
                      <input
                        type="text"
                        tabIndex={-1}
                        style={{ opacity: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
                        value={selectedCompanyId}
                        required
                        onChange={() => {}}
                      />

                      {/* Dropdown Trigger Box */}
                      <div
                        onClick={() => setIsCompanyDropdownOpen(prev => !prev)}
                        style={{
                          width: '100%',
                          minHeight: '38px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: isCompanyDropdownOpen ? '1px solid #3b5bfd' : '1px solid #cbd5e1',
                          boxShadow: isCompanyDropdownOpen ? '0 0 0 3px rgba(1, 157, 136, 0.15)' : 'none',
                          backgroundColor: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                          boxSizing: 'border-box'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1 }}>
                          <LuBuilding2 size={16} color={selectedCompanyObj ? '#3b5bfd' : '#94a3b8'} style={{ flexShrink: 0 }} />
                          {selectedCompanyObj ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {selectedCompanyObj.DisplayName || selectedCompanyObj.CompanyName}
                              </span>
                              <span style={{
                                fontSize: '0.72rem',
                                padding: '1px 6px',
                                borderRadius: '4px',
                                backgroundColor: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #e2e8f0',
                                fontWeight: 500,
                                flexShrink: 0
                              }}>
                                {selectedCompanyObj.CompanyCode || (selectedCompanyObj.IsSupplier ? 'Supplier' : (selectedCompanyObj.IsClient ? 'Client' : 'Company'))}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.88rem', color: '#94a3b8' }}>
                              -- Select Counterparty Company --
                            </span>
                          )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                          {selectedCompanyObj && (
                            <button
                              type="button"
                              onClick={handleClearCompany}
                              title="Clear selection"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#94a3b8',
                                borderRadius: '50%',
                                transition: 'color 0.15s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                            >
                              <LuX size={15} />
                            </button>
                          )}
                          <LuChevronDown
                            size={16}
                            color="#64748b"
                            style={{
                              transform: isCompanyDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}
                          />
                        </div>
                      </div>

                      {/* Floating Searchable Menu */}
                      {isCompanyDropdownOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 1050,
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden'
                          }}
                        >
                          {/* Search Input Bar */}
                          <div style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid #f1f5f9',
                            backgroundColor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <LuSearch size={15} color="#64748b" style={{ flexShrink: 0 }} />
                            <input
                              type="text"
                              autoFocus
                              placeholder="Search by company name, code..."
                              value={companySearchTerm}
                              onChange={(e) => setCompanySearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '100%',
                                border: 'none',
                                outline: 'none',
                                backgroundColor: 'transparent',
                                fontSize: '0.84rem',
                                color: '#0f172a'
                              }}
                            />
                            {companySearchTerm && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCompanySearchTerm('');
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '2px',
                                  color: '#94a3b8'
                                }}
                              >
                                <LuX size={14} />
                              </button>
                            )}
                          </div>

                          {/* Quick Stats / Result Count */}
                          <div style={{
                            padding: '4px 12px',
                            fontSize: '0.72rem',
                            color: '#64748b',
                            backgroundColor: '#f8fafc',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <span>Matches ({searchedCounterpartyCompanies.length})</span>
                            {selectedCompanyObj && (
                              <span style={{ color: '#3b5bfd', fontWeight: 650 }}>1 Selected</span>
                            )}
                          </div>

                          {/* Companies List */}
                          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                            {searchedCounterpartyCompanies.length === 0 ? (
                              <div style={{
                                padding: '24px 16px',
                                textAlign: 'center',
                                color: '#64748b',
                                fontSize: '0.84rem'
                              }}>
                                No companies found matching <strong>"{companySearchTerm}"</strong>
                              </div>
                            ) : (
                              searchedCounterpartyCompanies.map(c => {
                                const isSelected = String(c.CompanyId) === String(selectedCompanyId);
                                return (
                                  <div
                                    key={c.CompanyId}
                                    onClick={() => handleSelectCompany(c)}
                                    style={{
                                      padding: '8px 12px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#ecfdf5' : 'transparent',
                                      borderBottom: '1px solid #f8fafc',
                                      transition: 'background-color 0.1s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                      <span style={{
                                        fontSize: '0.86rem',
                                        fontWeight: isSelected ? 600 : 500,
                                        color: isSelected ? '#065f46' : '#1e293b'
                                      }}>
                                        {c.DisplayName || c.CompanyName}
                                      </span>
                                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                        {c.CompanyCode || (c.IsSupplier ? 'Supplier' : (c.IsClient ? 'Client' : 'Company'))}
                                        {c.Industry ? ` • ${c.Industry}` : ''}
                                      </span>
                                    </div>
                                    {isSelected && (
                                      <LuCheck size={16} color="#3b5bfd" style={{ flexShrink: 0 }} />
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Agreement Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={draftForm.title}
                        onChange={(e) => setDraftForm({ ...draftForm, title: e.target.value })}
                        placeholder={`e.g. Master Services Agreement — ${counterpartyCompanyName || 'Supplier'}`}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ position: 'relative' }} ref={templateDropdownRef}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Template
                      </label>

                      {/* Dropdown Trigger - Rounded Box */}
                      <div
                        onClick={() => setIsTemplateDropdownOpen(prev => !prev)}
                        style={{
                          width: '100%',
                          minHeight: '38px',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: isTemplateDropdownOpen ? '1px solid #3b5bfd' : '1px solid #cbd5e1',
                          boxShadow: isTemplateDropdownOpen ? '0 0 0 3px rgba(1, 157, 136, 0.15)' : 'none',
                          backgroundColor: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                          boxSizing: 'border-box'
                        }}
                      >
                        <span style={{
                          fontSize: '0.88rem',
                          fontWeight: 500,
                          color: '#0f172a',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden'
                        }}>
                          {(() => {
                            const found = templates.find(t => String(t.CommercialTemplateId) === String(draftForm.templateId));
                            if (found) {
                              return `${found.TemplateName}${found.IsDefault ? ' (Default)' : ''}`;
                            }
                            return `Default Standard ${documentTypeCode} Template`;
                          })()}
                        </span>

                        <LuChevronDown
                          size={16}
                          color="#64748b"
                          style={{
                            transform: isTemplateDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            flexShrink: 0,
                            marginLeft: '8px'
                          }}
                        />
                      </div>

                      {/* Floating Rounded Box Menu */}
                      {isTemplateDropdownOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 1050,
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden',
                            maxHeight: '220px',
                            overflowY: 'auto'
                          }}
                        >
                          {templates.length === 0 ? (
                            <div
                              onClick={() => {
                                setDraftForm(prev => ({ ...prev, templateId: '' }));
                                setIsTemplateDropdownOpen(false);
                              }}
                              style={{
                                padding: '9px 12px',
                                fontSize: '0.86rem',
                                color: '#1e293b',
                                cursor: 'pointer',
                                backgroundColor: !draftForm.templateId ? '#eff6ff' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}
                            >
                              <span>Default Standard {documentTypeCode} Template</span>
                              {!draftForm.templateId && <LuCheck size={16} color="#3b5bfd" />}
                            </div>
                          ) : (
                            templates.map(t => {
                              const isSelected = String(draftForm.templateId) === String(t.CommercialTemplateId);
                              return (
                                <div
                                  key={t.CommercialTemplateId}
                                  onClick={() => {
                                    setDraftForm(prev => ({ ...prev, templateId: t.CommercialTemplateId }));
                                    setIsTemplateDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '9px 12px',
                                    fontSize: '0.86rem',
                                    fontWeight: isSelected ? 600 : 500,
                                    color: isSelected ? '#065f46' : '#1e293b',
                                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    transition: 'background-color 0.12s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f5f9';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>{t.TemplateName}</span>
                                    {t.IsDefault && (
                                      <span style={{
                                        fontSize: '0.72rem',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: '#ecfdf5',
                                        color: '#1d4ed8',
                                        border: '1px solid #bfdbfe',
                                        fontWeight: 600
                                      }}>
                                        Default
                                      </span>
                                    )}
                                  </div>
                                  {isSelected && <LuCheck size={16} color="#3b5bfd" style={{ flexShrink: 0 }} />}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        Start Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={draftForm.effectiveFromDate}
                        onChange={(e) => setDraftForm({ ...draftForm, effectiveFromDate: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
                        End Date
                      </label>
                      <input
                        type="date"
                        value={draftForm.effectiveToDate}
                        onChange={(e) => setDraftForm({ ...draftForm, effectiveToDate: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {documentTypeCode === 'PO' && (
                    <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                      <div style={{ fontWeight: 650, fontSize: '0.88rem', color: '#0f172a', marginBottom: '10px' }}>
                        Purchase Order Engagement Details
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Consultant Name</label>
                          <input
                            type="text"
                            value={draftForm.consultantName}
                            onChange={(e) => setDraftForm({ ...draftForm, consultantName: e.target.value })}
                            style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                            {counterpartySide === 'SUPPLIER' ? 'Supplier Rate ($/hr)' : 'Bill Rate ($/hr)'}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={draftForm.billRate}
                            onChange={(e) => setDraftForm({ ...draftForm, billRate: e.target.value })}
                            style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                    <button
                      type="button"
                      onClick={onClose}
                      style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{ padding: '8px 20px', background: '#3b5bfd', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 650, cursor: loading ? 'wait' : 'pointer' }}
                    >
                      {loading ? 'Creating...' : `Create ${documentTypeCode} Draft`}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommercialDocumentModal;
